// src/context/DataContext.js
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

import { useAuth } from './AuthContext';
import { actionTypes, dataReducer } from './dataReducer'; // Import actionTypes and reducer
import { ERROR_CODES, ERROR_MESSAGES } from '../constants/errors'; // Import error codes and messages
import { formatDateToYYYYMMDD } from '../utils/HelperFunctions';
import { formatApiError, formatFirebaseError } from '../utils/errorUtils';

// API ENDPOINTS IMPORTS FOR THE MAIN NAVBAR MENU

import { apiHealthCheck } from '../api/PublicApi';

import { apiCheckCompliance, apiAcceptAgreement } from '../api/LegalApi';

import {
  fetchVisualizationData as apiFetchVisualizationData,
  fetchUserInvoices as apiFetchUserInvoices,
  fetchCompanyWalletTransactions as apiFetchCompanyWalletTransactions,
  createStripeCheckoutSession as apiCreateStripeCheckoutSession,
  createStripeSetupSession as apiCreateStripeSetupSession,
  fetchAutoTopupConfig as apiFetchAutoTopupConfig,
  updateAutoTopupConfig as apiUpdateAutoTopupConfig,
} from '../api/AnalyticsApi';

import {
  createTask as apiCreateTask,
  updateTask as apiUpdateTask,
  deleteTask as apiDeleteTask,
  rescheduleTasks as apiRescheduleTasks,
  fetchPendingEmailTask as apiFetchPendingEmailTask,
  fetchTasks as apiFetchTasks,
  fetchInfluencerQueryResults as apiFetchInfluencerQueryResults,
  fetchBatchInfluencerData as apiFetchBatchInfluencerData,
} from '../api/ToDoPageApi';

import {
  importDiscoveredInfluencers as apiImportDiscoveredInfluencers,
  permanentlyDiscardInfluencers as apiPermanentlyDiscardInfluencers, // Renamed
  moveInfluencersToHold as apiMoveInfluencersToHold, // New
  batchRefreshOnHoldInfluencers as apiBatchRefreshOnHoldInfluencers,
  analyzeOnHoldInfluencer as apiAnalyzeOnHoldInfluencer, // New
  singleRefreshOnHoldInfluencer as apiSingleRefreshOnHoldInfluencer,
} from '../api/DiscoveryPageApi';

import { fetchSingleQuery, fetchMultipleQuery } from '../api/QueryPageApi';

import {
  fetchInfluencerPageData as apiFetchInfluencerPageData,
  fetchSocialMedias as apiFetchSocialMedias,
  fetchSocialContacts as apiFetchSocialContacts,
  addSocialContact as apiAddSocialContact,
  addSocialMedia as apiAddSocialMedia,
  updateSocialContact as apiUpdateSocialContact,
  updateSocialMedia as apiUpdateSocialMedia,
  updatePartnerInfo as apiUpdatePartnerInfo,
  addNewInfluencer as apiAddNewInfluencer,
  getInfluencerDetails as apiGetInfluencerDetails,
  deleteInfluencer as apiDeleteInfluencer,
  refreshInfluencerHealth as apiRefreshInfluencerHealth,
  fetchInfluencerHealthDashboard as apiFetchInfluencerHealthDashboard, // NEW
  refreshInfluencerContentFocus as apiRefreshInfluencerContentFocus, // NEW
  fetchInfluencerContentFocusDashboard as apiFetchInfluencerContentFocusDashboard, // NEW
} from '../api/InfluencerPageApi';

import {
  fetchPublicationsByInfluencer as apiFetchPublicationsByInfluencer,
  fetchProductsForPublicationPage as apiFetchProductsToPublish,
  createPublication as apiCreatePublication,
  deletePublication as apiDeletePublication,
  addPublishedProduct as apiAddPublishedProduct,
  deletePublishedProduct as apiDeletePublishedProduct,
  togglePublishedProduct as apiTogglePublishedProduct,
  toggleIncludeVideoData as apiToggleIncludeVideoData,
  refreshPublicationMetadata as apiRefreshPublicationMetadata,
  generatePublicationAISummary as apiGeneratePublicationAISummary,
  fetchPublicationAiSummary as apiFetchPublicationAiSummary,
  updatePublicationAISummary as apiUpdatePublicationAISummary,
  generatePrivatePublicationAISummary as apiGeneratePrivatePublicationAISummary,
  fetchAggregatedMarketFit as apiFetchAggregatedMarketFit,
  fetchAggregatedPublicationSummary as apiFetchAggregatedPublicationSummary,
  fetchIndividualMarketFitAnalysis as apiFetchIndividualMarketFitAnalysis,
  generateSingleMarketFitAnalysis as apiGenerateSingleMarketFitAnalysis,
} from '../api/PublicationPageApi';

import {
  connectGoogle as apiConnectGoogle,
  connectMicrosoft365 as apiConnectMicrosoft365, // <-- ADDED
  connectYouTube as apiConnectYouTube,
  connectMeta as apiConnectMeta,
  connectTelegram as apiConnectTelegram,
  disconnectGoogle as apiDisconnectGoogle,
  disconnectMicrosoft365 as apiDisconnectMicrosoft365, // <-- ADDED
  disconnectYouTube as apiDisconnectYouTube,
  disconnectMeta as apiDisconnectMeta,
  disconnectTelegram as apiDisconnectTelegram,
  fetchSocialCredentials as apiFetchSocialCredentials,
  listGoogleAccounts as apiListGoogleAccounts,
  listMicrosoftAccounts as apiListMicrosoftAccounts,
  listYouTubeAccounts as apiListYouTubeAccounts,
  listMetaAccounts as apiListMetaAccounts,
  listTelegramAccounts as apiListTelegramAccounts,
  getGoogleStatus as apiGetGoogleStatus,
  getMicrosoft365Status as apiGetMicrosoft365Status,
  getMetaStatus as apiGetMetaStatus,
  getTelegramStatus as apiGetTelegramStatus,
  refreshGoogleToken as apiRefreshGoogleToken,
  refreshMicrosoft365Token as apiRefreshMicrosoft365Token,
  refreshYouTubeToken as apiRefreshYouTubeToken,
  refreshMetaToken as apiRefreshMetaToken,
  generateMessagesSummary as apiGenerateMessagesSummary,
  fetchMessagesSummary as apiFetchMessagesSummary,
  initTelegramAuth as apiInitTelegramAuth,
  completeTelegramAuth as apiCompleteTelegramAuth,
  completeTelegram2FAAuth as apiCompleteTelegram2FAAuth,
  listMicrosoft365Accounts,
} from '../api/MessagesPageApi';

import {
  generateAISummary as apiGenerateAISummary,
  fetchInfluencerAiSummary as apiFetchInfluencerAiSummary,
  updateAISummary as apiUpdateAISummary,
} from '../api/KnowledgePageApi';

import {
  updateActiveChannel as apiUpdateActiveChannel,
  updateInfluencerStatus as apiUpdateInfluencerStatus,
  fetchNewEmails as apiFetchNewEmails,
  generateEmailDraft as apiGenerateEmailDraft,
  translateEmailDraft as apiTranslateEmailDraft,
  simpleTranslation as apiSimpleTranslation,
  sendEmail as apiSendEmail,
  obtainNewPartnerData as apiObtainNewPartnerData,
  saveConfirmedPartnerData as apiSaveConfirmedPartnerData,
} from '../api/EmailPageApi';

import {
  fetchLLMOptions as apiFetchLLMOptions,
  fetchChats as apiFetchChats,
  fetchChatHistory as apiFetchChatHistory,
  createChat as apiCreateChat,
  sendMessage as apiSendMessage,
  setChatTitle as apiSetChatTitle,
  deleteChat as apiDeleteChat,
} from '../api/AiToolsApi';

// API ENDPOINTS IMPORTS FOR THE MAIN SIDEBAR MENU
import {
  fetchAllCountries as apiFetchAllCountries,
  fetchDiscoverySettings as apiFetchDiscoverySettings,
  updateDiscoverySettings as apiUpdateDiscoverySettings,
  generateDiscoveryKeywords as apiGenerateDiscoveryKeywords,
} from '../api/DiscoverySettingsApi';

import {
  fetchTaskTemplates as apiFetchTaskTemplates,
  updateTaskTemplate as apiUpdateTaskTemplate,
  fetchLlmConfigurations as apiFetchLlmConfigurations,
  fetchUserSettings as apiFetchUserSettings,
  updateUserSettings as apiUpdateUserSettings,
  fetchAdminLlmConfigurations as apiFetchAdminLlmConfigurations,
  addLlmConfiguration as apiAddLlmConfiguration,
  updateLlmConfiguration as apiUpdateLlmConfiguration,
  deleteLlmConfiguration as apiDeleteLlmConfiguration,
  fetchUserApiKeyStatus as apiFetchUserApiKeyStatus,
  upsertUserApiKey as apiUpsertUserApiKey,
  deleteUserApiKey as apiDeleteUserApiKey,
} from '../api/llmConfigSidebarApi';

// --- NEW Import API Calls for Admin Tasks ---
import {
  apiFetchCompanyData,
  apiUpdateCompanyData,
} from '../api/CompanySidebarApi';

import {
  fetchProducts as apiFetchProducts,
  createProduct as apiCreateProduct,
  updateProduct as apiUpdateProduct,
  deleteProduct as apiDeleteProduct,
} from '../api/productSidebarApi';

import {
  fetchUsers as apiFetchUsers, // GET /auth/api/users
  adminSignupUser as apiAdminSignupUser, // POST /auth/api/signupUser
  moveInfluencers as apiMoveInfluencers, // POST /auth/api/moveInfluencers
  deleteUserAdmin as apiDeleteUserAdmin, // DELETE /auth/api/deleteUser/<uid> (Admin Version)
} from '../api/AdminTasksSidebarApi';

import {
  apiOnboardCompany,
  apiImpersonateUser,
  apiFetchAllSystemUsers,
  apiFetchSystemInvoices,
  apiMarkInvoicePaid,
  apiGenerateInvoices,
  apiFetchSystemWalletTransactions,
  apiSuperAdminScrapeWebsite,
} from '../api/SuperAdminApi';

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  // Define initial state OUTSIDE of useReducer
  const initialState = {
    mockData: [],
    queryResults: [],
    lastQuery: null,
    queryTableSorting: [],
    activeinfluencerId: null,
    publications: [],
    // --- NEW Company State ---
    isCompanyModalOpen: false,
    companyData: null, // Holds { companyName, companyDescription, logoURL, companyPolicy }
    isCompanyModalLoading: false,
    companyModalError: null,
    // --- End NEW ---
    products: [], // List of all products { productsListId, productName, description }
    isProductModalOpen: false,
    selectedProductId: null, // ID of product selected in modal, or 'new'
    isProductModalLoading: false, // Loading state specifically for modal actions
    productModalError: null, // Error state specifically for modal actions
    emailData: [],
    tasks: [],
    selectedDate: formatDateToYYYYMMDD(new Date()),
    isQueryMode: true,
    taskTemplates: [], // Holds task template configurations from DB (using JS keys like taskType)
    llmConfigs: {}, // Stores { provider: { idiom: [models] } }
    userSettings: null, // Stores { userId, defaultLlmConfigId }
    isLlmConfigModalOpen: false,
    llmConfigModalLoading: false, // Loading state specifically for LLM modal saves
    llmConfigModalError: null, // Error state specifically for LLM modal saves
    adminLlmConfigsList: [], // Full list for admin modal [{ llmConfigId, ... }]
    isLlmAdminModalOpen: false,
    llmAdminModalLoading: false,
    llmAdminModalError: null,
    chats: [],
    aiChats: [], // Initialize AI chats list
    chatInProgress: false,
    isLoadingQueryData: false,
    userProfile: null,
    isUserProfileModalOpen: false,
    isUserProfileModalLoading: false, // Local loading for user profile modal
    userProfileModalError: null, // Local error for user profile modal
    loading: false,
    isLoading: false,
    error: null, // ✅ General error state (can be a string, object, etc.)
    apiErrors: {}, // ✅ Store individual API errors (optional, but useful)
    isOffline: false,
    isRetrying: false,
    socialMediaConnections: {},
    reconnectNetwork: null, // Add this to initialState
    metaTokenExpired: false,
    emailPageCache: {}, // Stores work-in-progress data keyed by influencerId
    // --- NEW Admin Tasks State ---
    isAdminTasksModalOpen: false, // Controls Admin Tasks Modal visibility
    adminTasksLoading: false, // Loading state for Admin Tasks Modal operations
    adminTasksError: null, // Error state for Admin Tasks Modal operations
    allUsers: [], // List of all users for Admin dropdowns
    onHoldInfluencers: [],
    // --- NEW Discovery Settings State ---
    isDiscoverySettingsModalOpen: false,
    discoverySettingsLoading: false,
    discoverySettingsError: null,
    discoverySettings: null, // Holds { excluded_countries: [], description_keywords_text: "" }
    discoverySorting: {
      new: [],
      similar: [],
      on_hold: [],
      existing: [],
      discarded: [],
    },
    isMarketFitModalOpen: false,
    marketFitDashboardData: null,
    isMarketFitDashboardLoading: false,
    isContentFocusModalOpen: false,
    contentFocusDashboardData: null,
    isContentFocusDashboardLoading: false,
    aggregatedMarketFit: null,
    aggregatedPublicationSummary: null,
    individualMarketFitAnalysis: {},
    // --- NEW: Smart Dashboard Analytics State ---
    isAnalyticsModalOpen: false,
    analyticsDashboardLoading: false,
    analyticsDashboardError: null,
    analyticsDashboardData: null, // Holds the full response from the backend
    lastAnalyticsQuery: null, // For saving/restoring the last viewed chart
    // --- NEW: AI Agent State ---
    aiAgentPageCache: {}, // Stores { selectedInfluencers, selectedTask, jobName, prompt }
    // --- NEW SuperAdmin State ---
    isSuperAdminModalOpen: false,
    superAdminLoading: false,
    superAdminError: null,
    systemUsers: [],
    systemInvoices: [],
    userInvoices: [],
    companyWalletTransactions: [],
    systemWalletTransactions: [],
    autoTopupConfig: null,
    autoTopupLoading: false,
    analyticsInitialTab: 'usage',
    isStripeLoading: false,
  };

  const { authLoading, loginWithCustomToken } = useAuth();
  const location = useLocation();

  const [state, dispatch] = useReducer(
    dataReducer,
    initialState, // Pass initial state as the second argument
    (initial) => {
      try {
        const storedState = localStorage.getItem('appState');
        console.log('DEBUG INIT: storedState from localStorage:', storedState);
        if (storedState) {
          const parsedState = JSON.parse(storedState);
          console.log('DEBUG INIT: parsedState:', parsedState);

          // --- Create the final state by merging carefully ---
          // Start with the defined defaults
          const finalInitialState = { ...initial };

          // Iterate over keys in the parsed state and merge ONLY if the key
          // exists in our defined initialState, preserving the type.
          for (const key in parsedState) {
            if (Object.hasOwnProperty.call(initial, key)) {
              // Check if key is part of our intended state
              const initialValue = initial[key];
              const storedValue = parsedState[key];

              // ---> THE FIX: Never restore loading spinners or open modals on hard reloads <---
              const transientKeys = [
                'superAdminLoading',
                'isSuperAdminModalOpen',
                'adminTasksLoading',
                'isAdminTasksModalOpen',
                'isCompanyModalOpen',
                'isCompanyModalLoading',
                'isProductModalOpen',
                'isProductModalLoading',
                'isLlmConfigModalOpen',
                'llmConfigModalLoading',
                'isLlmAdminModalOpen',
                'llmAdminModalLoading',
                'isUserProfileModalOpen',
                'isUserProfileModalLoading',
                'isAnalyticsModalOpen',
                'analyticsDashboardLoading',
                'isMarketFitModalOpen',
                'isMarketFitDashboardLoading',
                'isContentFocusModalOpen',
                'isContentFocusDashboardLoading',
                'isDiscoverySettingsModalOpen',
                'discoverySettingsLoading',
                'loading',
                'isLoading',
                'chatInProgress',
              ];

              if (transientKeys.includes(key)) {
                finalInitialState[key] = initialValue; // Force back to default (false)
                continue;
              }
              // Basic type preservation (can be expanded)
              if (
                typeof storedValue === typeof initialValue ||
                initialValue === null
              ) {
                // Special handling for Date
                if (key === 'selectedDate') {
                  finalInitialState[key] =
                    storedValue && !isNaN(new Date(storedValue))
                      ? new Date(storedValue)
                      : new Date(); // Fallback
                }
                // Special handling for booleans (ensure they are boolean)
                else if (typeof initialValue === 'boolean') {
                  finalInitialState[key] =
                    storedValue === 'true' || storedValue === true;
                }
                // Special handling for arrays
                else if (Array.isArray(initialValue)) {
                  finalInitialState[key] = Array.isArray(storedValue)
                    ? storedValue
                    : initialValue;
                }
                // Special handling for objects (simple merge, could be deeper)
                else if (
                  typeof initialValue === 'object' &&
                  initialValue !== null &&
                  !Array.isArray(initialValue)
                ) {
                  finalInitialState[key] =
                    typeof storedValue === 'object' && storedValue !== null
                      ? storedValue
                      : initialValue;
                }
                // Handle null explicitly
                else if (initialValue === null) {
                  finalInitialState[key] = storedValue; // Allow stored value (could be null or data)
                }
                // Other types (string, number)
                else {
                  finalInitialState[key] = storedValue;
                }
              } else {
                console.warn(
                  `DEBUG INIT: Type mismatch for key "${key}" in localStorage. Stored: ${typeof storedValue}, Expected: ${typeof initialValue}. Using default.`,
                );
                // Keep the default value from initial if types mismatch
              }
            } else {
              console.warn(
                `DEBUG INIT: Key "${key}" found in localStorage but not in initialState. Ignoring.`,
              );
            }
          }
          console.log(
            'DEBUG INIT: Returning final merged state:',
            finalInitialState,
          );
          return finalInitialState;
        }
        console.log(
          'DEBUG INIT: No state found in localStorage, using initial.',
        );
        return initial; // Use initialState if nothing in localStorage
      } catch (e) {
        console.warn('Failed to parse state from localStorage:', e);
        localStorage.removeItem('appState'); // Clear corrupted storage
        return initial; // Return initial state on error
      }
    },
  );
  //Sync with localStorage
  useEffect(() => {
    try {
      localStorage.setItem('appState', JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save state to localStorage:', e);
    }
  }, [state]);

  // ============================================================
  // === ALL DECONSTRUCTED STATES (Corrected & Production Ready) ===
  // ============================================================

  const { currentUser } = useAuth();
  const [productToggles, setProductToggles] = useState([]);

  const {
    activeinfluencerId,
    error,
    isOffline,
    isRetrying,
    queryResults,
    isLoadingQueryData,
    userProfile,
    isSuperAdminModalOpen,
    superAdminLoading,
    superAdminError,
    systemUsers,
    systemInvoices,
    userInvoices,
    companyWalletTransactions,
    systemWalletTransactions,
  } = state;

  // ============================================================
  // === ALL STATE DISPATCHERS (Corrected & Production Ready) ===
  // ============================================================

  // this is the only setter that uses 'function' due to the need to update queryResults from DataContext
  const setQueryResults = useCallback(
    (resultsOrCallback) => {
      // Check if an updater function was passed
      if (typeof resultsOrCallback === 'function') {
        // If it's a function, dispatch the UPDATE action with the function as the payload.
        // The reducer will handle calling it with the latest state.
        dispatch({
          type: actionTypes.UPDATE_QUERY_RESULTS,
          payload: resultsOrCallback,
        });
      } else {
        // If it's a direct value, dispatch the SET action as before.
        dispatch({
          type: actionTypes.SET_QUERY_RESULTS,
          payload: resultsOrCallback,
        });
      }
    },
    [dispatch],
  ); // <-- The dependency array is now perfectly stable.

  const setLastQuery = useCallback((query) => {
    dispatch({ type: actionTypes.SET_LAST_QUERY, payload: query });
  }, []);

  const setQueryTableSorting = useCallback(
    (sorting) => {
      dispatch({ type: actionTypes.SET_QUERY_TABLE_SORTING, payload: sorting });
    },
    [dispatch],
  );

  const setActiveinfluencerId = useCallback(
    (id) => {
      dispatch({ type: actionTypes.SET_ACTIVE_CHANNEL_ID, payload: id });
    },
    [dispatch],
  );

  // --- NEW: setActiveChannel ---
  const setActiveChannel = useCallback(
    (influencerId, channel) => {
      // Takes influencerId AND channel
      dispatch({
        type: actionTypes.SET_ACTIVE_CHANNEL,
        payload: { influencerId, channel },
      });
    },
    [dispatch],
  );

  // --- NEW: setActiveInfluencerAndChannel ---
  const setActiveInfluencerAndChannel = useCallback(
    (id, channel) => {
      dispatch({
        type: actionTypes.SET_ACTIVE_INFLUENCER_AND_CHANNEL,
        payload: { id, channel },
      });
    },
    [dispatch],
  );

  // --- NEW: setActiveInfluencerStatus ---
  const setStatus = useCallback(
    (influencerId, status) => {
      // Takes influencerId AND channel
      dispatch({
        type: actionTypes.SET_ACTIVE_STATUS,
        payload: { influencerId, status },
      });
    },
    [dispatch],
  );

  const setSocialMediaConnections = useCallback(
    (connections) => {
      dispatch({
        type: actionTypes.SET_SOCIAL_MEDIA_CONNECTIONS,
        payload: connections,
      });
    },
    [dispatch],
  );

  const setIsOffline = useCallback(
    (offlineStatus) => {
      dispatch({ type: actionTypes.SET_IS_OFFLINE, payload: offlineStatus });
    },
    [dispatch],
  );

  const setReconnectNetwork = useCallback(
    (network) => {
      dispatch({
        type: actionTypes.SET_RECONNECT_NETWORK,
        payload: network,
      });
    },
    [dispatch],
  );

  const setPublications = useCallback(
    (publications) => {
      dispatch({ type: actionTypes.SET_PUBLICATIONS, payload: publications });
    },
    [dispatch],
  );

  const setPublicationRefreshingMetadata = useCallback(
    (publicationId, isRefreshing) => {
      dispatch({
        type: actionTypes.SET_PUBLICATION_REFRESHING_METADATA,
        payload: { publicationId, isRefreshing },
      });
    },
    [dispatch],
  );

  const updatePublicationDetails = useCallback(
    (publication) => {
      dispatch({
        type: actionTypes.UPDATE_PUBLICATION_DETAILS,
        payload: publication,
      });
    },
    [dispatch],
  );

  const setTasks = useCallback(
    (tasks) => {
      dispatch({ type: actionTypes.SET_TASKS, payload: tasks });
    },
    [dispatch],
  );

  const addTask = useCallback(
    (newTask) => {
      dispatch({ type: actionTypes.ADD_TASK, payload: newTask });
    },
    [dispatch],
  );

  const toggleTaskCompletion = useCallback(
    (taskId) => {
      dispatch({ type: actionTypes.TOGGLE_TASK_COMPLETION, payload: taskId });
    },
    [dispatch],
  );

  const updateTasksDate = useCallback(
    (todoIds, newDate) => {
      dispatch({
        type: actionTypes.UPDATE_TASKS_DATE,
        payload: { todoIds, newDate },
      });
    },
    [dispatch],
  );

  const deleteTaskById = useCallback(
    (taskId) => {
      dispatch({ type: actionTypes.DELETE_TASK, payload: taskId });
    },
    [dispatch],
  );

  const setSelectedDate = useCallback(
    (date) => {
      dispatch({ type: actionTypes.SET_SELECTED_DATE, payload: date });
    },
    [dispatch],
  );

  const setQueryMode = useCallback(
    (mode) => {
      dispatch({ type: actionTypes.SET_QUERY_MODE, payload: mode });
    },
    [dispatch],
  );

  const setIsLoadingQueryData = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_ISLOADING_QUERY_DATA,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  // New Company dispatchers
  const setIsCompanyModalOpen = useCallback(
    (isOpen) => {
      dispatch({ type: actionTypes.SET_COMPANY_MODAL_OPEN, payload: isOpen });
    },
    [dispatch],
  );

  const setCompanyData = useCallback(
    (data) => {
      dispatch({ type: actionTypes.SET_COMPANY_DATA, payload: data });
    },
    [dispatch],
  );

  const setIsCompanyModalLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_COMPANY_MODAL_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setCompanyModalError = useCallback(
    (error) => {
      dispatch({ type: actionTypes.SET_COMPANY_MODAL_ERROR, payload: error });
    },
    [dispatch],
  );
  // --- End NEW Company Dispatchers ---

  // --- NEW Config Dispatchers ---
  const setTaskTemplates = useCallback(
    (templates) => {
      dispatch({ type: actionTypes.SET_TASK_TEMPLATES, payload: templates });
    },
    [dispatch],
  );

  const updateTaskTemplateInList = useCallback(
    (updatedTemplateData) => {
      // Requires taskType in the payload
      dispatch({
        type: actionTypes.UPDATE_TASK_TEMPLATE_IN_LIST,
        payload: updatedTemplateData,
      });
    },
    [dispatch],
  );

  const setIsLlmConfigModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_LLM_CONFIG_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setLlmConfigModalLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_LLM_CONFIG_MODAL_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setLlmConfigModalError = useCallback(
    (error) => {
      dispatch({
        type: actionTypes.SET_LLM_CONFIG_MODAL_ERROR,
        payload: error,
      });
    },
    [dispatch],
  );

  const setLlmConfigs = useCallback(
    (configs) => {
      dispatch({ type: actionTypes.SET_LLM_CONFIGURATIONS, payload: configs });
    },
    [dispatch],
  ); // Renamed for clarity

  // --- NEW: Dispatchers for Email Page Cache ---
  const updateEmailPageWork = useCallback(
    (influencerId, workData) => {
      dispatch({
        type: actionTypes.UPDATE_EMAIL_PAGE_WORK,
        payload: { influencerId, workData },
      });
    },
    [dispatch],
  );

  const clearEmailPageWork = useCallback(
    (influencerId) => {
      dispatch({
        type: actionTypes.CLEAR_EMAIL_PAGE_WORK,
        payload: influencerId,
      });
    },
    [dispatch],
  );

  // --- Discovery Dispatchers ---

  const updateOnHoldInfluencer = useCallback(
    (updatedData) => {
      dispatch({
        type: actionTypes.UPDATE_ON_HOLD_INFLUENCER,
        payload: updatedData,
      });
    },
    [dispatch],
  );

  const addToOnHoldInfluencers = useCallback(
    (influencers) => {
      dispatch({
        type: actionTypes.ADD_TO_ON_HOLD_INFLUENCERS,
        payload: influencers,
      });
    },
    [dispatch],
  );

  const removeFromOnHoldInfluencers = useCallback(
    (ids) => {
      dispatch({
        type: actionTypes.REMOVE_FROM_ON_HOLD_INFLUENCERS,
        payload: ids,
      });
    },
    [dispatch],
  );
  const setDiscoverySorting = useCallback(
    (tab, updater) => {
      dispatch({
        type: actionTypes.SET_DISCOVERY_SORTING,
        payload: { tab, updater },
      });
    },
    [dispatch],
  );

  // --- NEW Discovery Settings Dispatchers ---
  const setIsDiscoverySettingsModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_DISCOVERY_SETTINGS_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setDiscoverySettingsLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_DISCOVERY_SETTINGS_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setDiscoverySettingsError = useCallback(
    (error) => {
      dispatch({
        type: actionTypes.SET_DISCOVERY_SETTINGS_ERROR,
        payload: error,
      });
    },
    [dispatch],
  );

  const setDiscoverySettings = useCallback(
    (settings) => {
      dispatch({ type: actionTypes.SET_DISCOVERY_SETTINGS, payload: settings });
    },
    [dispatch],
  );

  // --- NEW Market Fit Dispatchers ---
  const setIsMarketFitModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_MARKET_FIT_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setMarketFitDashboardData = useCallback(
    (data) => {
      dispatch({
        type: actionTypes.SET_MARKET_FIT_DASHBOARD_DATA,
        payload: data,
      });
    },
    [dispatch],
  );

  const setIsMarketFitDashboardLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_IS_MARKET_FIT_DASHBOARD_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  // --- NEW Content Focus Dispatchers ---
  const setIsContentFocusModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_CONTENT_FOCUS_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setContentFocusDashboardData = useCallback(
    (data) => {
      dispatch({
        type: actionTypes.SET_CONTENT_FOCUS_DASHBOARD_DATA,
        payload: data,
      });
    },
    [dispatch],
  );

  const setIsContentFocusDashboardLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_IS_CONTENT_FOCUS_DASHBOARD_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  // --- NEW Admin LLM Config Dispatchers ---
  const setAdminLlmConfigs = useCallback(
    (configs) =>
      dispatch({ type: actionTypes.SET_ADMIN_LLM_CONFIGS, payload: configs }),
    [dispatch],
  );
  const addAdminLlmConfig = useCallback(
    (config) =>
      dispatch({ type: actionTypes.ADD_ADMIN_LLM_CONFIG, payload: config }),
    [dispatch],
  );
  const updateAdminLlmConfig = useCallback(
    (config) =>
      dispatch({ type: actionTypes.UPDATE_ADMIN_LLM_CONFIG, payload: config }),
    [dispatch],
  );
  const deleteAdminLlmConfig = useCallback(
    (id) =>
      dispatch({ type: actionTypes.DELETE_ADMIN_LLM_CONFIG, payload: id }),
    [dispatch],
  );
  const setIsLlmAdminModalOpen = useCallback(
    (isOpen) =>
      dispatch({
        type: actionTypes.SET_IS_LLM_ADMIN_MODAL_OPEN,
        payload: isOpen,
      }),
    [dispatch],
  );
  const setLlmAdminModalLoading = useCallback(
    (isLoading) =>
      dispatch({
        type: actionTypes.SET_LLM_ADMIN_MODAL_LOADING,
        payload: isLoading,
      }),
    [dispatch],
  );
  const setLlmAdminModalError = useCallback(
    (error) =>
      dispatch({ type: actionTypes.SET_LLM_ADMIN_MODAL_ERROR, payload: error }),
    [dispatch],
  );

  const setUserSettings = useCallback(
    (settings) => {
      dispatch({ type: actionTypes.SET_USER_SETTINGS, payload: settings });
    },
    [dispatch],
  );

  // Function to add a chat to the global state (called after successful API creation)
  const addChat = useCallback(
    (newChatData) => {
      // Expects { chatId, title, updatedAt }
      dispatch({ type: actionTypes.ADD_AI_CHAT, payload: newChatData });
    },
    [dispatch],
  );

  // Function to remove a chat from the global state (called after successful API deletion)
  const deleteChatById = useCallback(
    (chatId) => {
      dispatch({ type: actionTypes.REMOVE_AI_CHAT, payload: chatId });
    },
    [dispatch],
  );

  // Function to update a chat title in the global state (called after successful API update)
  const updateChatTitle = useCallback(
    (chatId, newTitle) => {
      dispatch({
        type: actionTypes.UPDATE_AI_CHAT_TITLE,
        payload: { chatId, newTitle },
      });
    },
    [dispatch],
  );

  const setChatInProgress = useCallback(
    (inProgress) => {
      dispatch({ type: actionTypes.SET_CHAT_IN_PROGRESS, payload: inProgress });
    },
    [dispatch],
  );

  const setUserProfile = useCallback(
    (profile) => {
      console.log(
        'DEBUG DATACONTEXT: setUserProfile dispatcher called with:',
        profile,
      ); // <-- Log Step 6
      dispatch({ type: actionTypes.SET_USER_PROFILE, payload: profile });
    },
    [dispatch],
  );

  // --- NEW Admin Tasks Dispatchers ---
  const setIsAdminTasksModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_ADMIN_TASKS_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setAdminTasksLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_ADMIN_TASKS_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setAdminTasksError = useCallback(
    (error) => {
      dispatch({ type: actionTypes.SET_ADMIN_TASKS_ERROR, payload: error });
    },
    [dispatch],
  );

  const setAllUsers = useCallback(
    (users) => {
      dispatch({ type: actionTypes.SET_ALL_USERS, payload: users });
    },
    [dispatch],
  );

  const addUserToAllUsersList = useCallback(
    (user) => {
      dispatch({ type: actionTypes.ADD_USER_TO_ALL_USERS_LIST, payload: user });
    },
    [dispatch],
  );

  const removeUserFromAllUsersList = useCallback(
    (userId) => {
      dispatch({
        type: actionTypes.REMOVE_USER_FROM_ALL_USERS_LIST,
        payload: userId,
      });
    },
    [dispatch],
  );
  // --- End NEW Admin Tasks Dispatchers ---

  const setLoading = useCallback(
    (loading) => {
      dispatch({ type: actionTypes.SET_LOADING, payload: loading });
    },
    [dispatch],
  );

  // --- Error Handling Dispatchers ---
  const setError = useCallback(
    (error) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: error });
    },
    [dispatch],
  );

  const clearError = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_ERROR });
  }, [dispatch]);

  const setApiError = useCallback(
    (apiName, error) => {
      dispatch({
        type: actionTypes.SET_API_ERROR,
        payload: { apiName, error },
      });
    },
    [dispatch],
  );

  const clearApiError = useCallback(
    (apiName) => {
      dispatch({ type: actionTypes.CLEAR_API_ERROR, payload: apiName });
    },
    [dispatch],
  );

  // --- Token Expiration Dispatchers
  const setMetaTokenExpired = useCallback(
    (isExpired) => {
      dispatch({
        type: actionTypes.SET_META_TOKEN_EXPIRED,
        payload: isExpired,
      });
    },
    [dispatch],
  );

  // --- NEW Product Management Dispatchers ---
  const setProducts = useCallback(
    (products) => {
      dispatch({ type: actionTypes.SET_PRODUCTS, payload: products });
    },
    [dispatch],
  );

  const addProductToList = useCallback(
    (product) => {
      dispatch({ type: actionTypes.ADD_PRODUCT, payload: product });
    },
    [dispatch],
  );

  const updateProductInList = useCallback(
    (product) => {
      dispatch({ type: actionTypes.UPDATE_PRODUCT_IN_LIST, payload: product });
    },
    [dispatch],
  );

  const removeProductFromList = useCallback(
    (productId) => {
      dispatch({
        type: actionTypes.REMOVE_PRODUCT_FROM_LIST,
        payload: productId,
      });
    },
    [dispatch],
  );

  const setIsProductModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_PRODUCT_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setSelectedProductId = useCallback(
    (productId) => {
      dispatch({
        type: actionTypes.SET_SELECTED_PRODUCT_ID,
        payload: productId,
      });
    },
    [dispatch],
  );

  const setProductModalLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_PRODUCT_MODAL_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setProductModalError = useCallback(
    (error) => {
      dispatch({ type: actionTypes.SET_PRODUCT_MODAL_ERROR, payload: error });
    },
    [dispatch],
  );

  // --- NEW Publications Management Dispatchers ---
  const setPublicationTogglingVideo = useCallback(
    (publicationId, isToggling) => {
      dispatch({
        type: actionTypes.SET_PUBLICATION_TOGGLING_VIDEO,
        payload: { publicationId, isToggling },
      });
    },
    [dispatch],
  );

  const updatePublicationVideoData = useCallback(
    (publicationId, includeVideoData) => {
      dispatch({
        type: actionTypes.UPDATE_PUBLICATION_VIDEO_DATA,
        payload: { publicationId, includeVideoData },
      });
    },
    [dispatch],
  );

  const setPublicationGeneratingSummary = useCallback(
    (publicationId, isGenerating) => {
      dispatch({
        type: actionTypes.SET_PUBLICATION_GENERATING_SUMMARY,
        payload: { publicationId, isGenerating },
      });
    },
    [dispatch],
  );

  const setPublicationTogglingProduct = useCallback(
    (publicationId, productId, isToggling) => {
      dispatch({
        type: actionTypes.SET_PUBLICATION_TOGGLING_PRODUCT,
        payload: { publicationId, productId, isToggling },
      });
    },
    [dispatch],
  );

  const updatePublicationProducts = useCallback(
    (publicationId, updatedProducts) => {
      dispatch({
        type: actionTypes.UPDATE_PUBLICATION_PRODUCTS,
        payload: { publicationId, updatedProducts },
      });
    },
    [dispatch],
  );

  const setPublicationAiSummary = useCallback(
    (arg1, arg2) => {
      if (Array.isArray(arg1)) {
        // BATCH MODE: Pass the array directly as payload
        dispatch({
          type: actionTypes.SET_PUBLICATION_AI_SUMMARY,
          payload: arg1,
        });
      } else {
        // SINGLE MODE: Pass the object structure
        dispatch({
          type: actionTypes.SET_PUBLICATION_AI_SUMMARY,
          payload: { publicationId: arg1, pubAiSummary: arg2 },
        });
      }
    },
    [dispatch],
  );

  // --- HIGHLIGHT: New Dispatchers for Aggregated Data ---
  const clearAggregatedData = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_AGGREGATED_DATA });
  }, [dispatch]);

  // --- NEW User Profile Modal State ---
  const setIsUserProfileModalOpen = useCallback(
    (isOpen) => {
      console.log('[DataContext] SET_IS_USER_PROFILE_MODAL_OPEN:', isOpen);
      dispatch({
        type: actionTypes.SET_IS_USER_PROFILE_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );
  const setIsUserProfileModalLoading = useCallback(
    (isLoading) => {
      console.log(
        '[DataContext] SET_IS_USER_PROFILE_MODAL_LOADING:',
        isLoading,
      );
      dispatch({
        type: actionTypes.SET_IS_USER_PROFILE_MODAL_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setUserProfileModalError = useCallback(
    (error) => {
      console.error('[DataContext] SET_USER_PROFILE_MODAL_ERROR:', error);
      dispatch({
        type: actionTypes.SET_USER_PROFILE_MODAL_ERROR,
        payload: error,
      });
    },
    [dispatch],
  );

  // --- NEW: Smart Dashboard Analytics Dispatchers ---
  const setIsAnalyticsModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_ANALYTICS_MODAL_OPEN,
        payload: isOpen,
      });
      if (!isOpen) {
        dispatch({
          type: actionTypes.SET_ANALYTICS_INITIAL_TAB,
          payload: 'usage',
        }); // Reset tab
        dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: false }); // Reset spinner
      }
    },
    [dispatch],
  );

  const setAnalyticsDashboardLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_ANALYTICS_DASHBOARD_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setAnalyticsDashboardError = useCallback(
    (error) => {
      dispatch({
        type: actionTypes.SET_ANALYTICS_DASHBOARD_ERROR,
        payload: error,
      });
    },
    [dispatch],
  );

  const setAnalyticsDashboardData = useCallback(
    (data) => {
      dispatch({
        type: actionTypes.SET_ANALYTICS_DASHBOARD_DATA,
        payload: data,
      });
    },
    [dispatch],
  );

  const setLastAnalyticsQuery = useCallback(
    (query) => {
      dispatch({ type: actionTypes.SET_LAST_ANALYTICS_QUERY, payload: query });
    },
    [dispatch],
  );

  const setAnalyticsSelectedChartType = useCallback(
    (chartType) => {
      dispatch({
        type: actionTypes.SET_ANALYTICS_SELECTED_CHART_TYPE,
        payload: chartType,
      });
    },
    [dispatch],
  );

  const setIndividualMarketFitAnalysis = useCallback(
    (payload) => {
      dispatch({
        type: actionTypes.SET_INDIVIDUAL_MARKET_FIT_ANALYSIS, // Ensure this type exists in dataReducer
        payload: payload,
      });
    },
    [dispatch],
  );

  const setAggregatedMarketFit = useCallback(
    (payload) => {
      dispatch({
        type: actionTypes.SET_AGGREGATED_MARKET_FIT, // Ensure this type exists in dataReducer
        payload: payload,
      });
    },
    [dispatch],
  );

  const setAggregatedPublicationSummary = useCallback(
    (payload) => {
      dispatch({
        type: actionTypes.SET_AGGREGATED_PUBLICATION_SUMMARY, // Ensure this type exists
        payload: payload,
      });
    },
    [dispatch],
  );

  // --- NEW: Dispatchers for AI Agent Page Cache ---
  const updateAiAgentPageCache = useCallback(
    (data) => {
      dispatch({
        type: actionTypes.UPDATE_AI_AGENT_PAGE_CACHE,
        payload: data,
      });
    },
    [dispatch],
  );

  const clearAiAgentPageCache = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_AI_AGENT_PAGE_CACHE });
  }, [dispatch]);

  // 5. Create Dispatchers (add these with your other dispatchers):
  const setIsSuperAdminModalOpen = useCallback(
    (isOpen) => {
      dispatch({
        type: actionTypes.SET_IS_SUPERADMIN_MODAL_OPEN,
        payload: isOpen,
      });
    },
    [dispatch],
  );

  const setSuperAdminLoading = useCallback(
    (isLoading) => {
      dispatch({
        type: actionTypes.SET_SUPERADMIN_LOADING,
        payload: isLoading,
      });
    },
    [dispatch],
  );

  const setSuperAdminError = useCallback(
    (error) => {
      dispatch({ type: actionTypes.SET_SUPERADMIN_ERROR, payload: error });
    },
    [dispatch],
  );

  const setSystemUsers = useCallback(
    (users) => {
      dispatch({ type: actionTypes.SET_SYSTEM_USERS, payload: users });
    },
    [dispatch],
  );

  // =======================================================================
  // === GENERAL API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  // Fetch User Profile (Called during initial auth check in AppContent and after updates)
  const retrieveUserProfile = useCallback(async () => {
    if (!currentUser) {
      console.warn(
        '[DataContext] retrieveUserProfile called but no currentUser.',
      );
      setUserProfile(null); // Ensure userProfile is null if no user
      setLoading(false); // Stop loading if called without user
      return;
    }
    console.log(
      '[DataContext] Attempting to retrieveUserProfile for UID:',
      currentUser.uid,
    );
    /*setLoading(true);*/
    setError(null); // Clear main error
    try {
      const baseUrl =
        process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const token = await currentUser.getIdToken(true); // Force refresh token
      const response = await axios.get(
        `${baseUrl}/auth/api/retrieveUserProfile/${currentUser.uid}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // --- NEW DEBUG LOG ---
      console.log('[DataContext] Financial Status Received:', {
        status: response.data.accountStatus,
        daysLeft: response.data.gracePeriodDaysLeft,
        link: response.data.latestInvoiceLink,
      });
      console.log('[DataContext] retrieveUserProfile success:', response.data);
      setUserProfile(response.data); // Update userProfile state
      setLoading(false);
      return { status: 'success', data: response.data };
    } catch (err) {
      // 'err' is the Firebase error object from above.
      const formattedError = formatFirebaseError(err, 'retrieveUserProfile'); // <-- Call our complete utility
      setError(formattedError);
      setUserProfile(null); // Clear profile on fetch error
      setLoading(false);
      return formattedError;
    }
  }, [currentUser, setLoading, setError, setUserProfile]);

  // --- NEW API Update User Profile ---
  const updateUserProfile = useCallback(
    async (uid, payload) => {
      if (!currentUser || currentUser.uid !== uid) {
        const msg = 'Unauthorized attempt to update user profile.';
        console.error(`[DataContext] ${msg}`);
        setUserProfileModalError(msg); // Use modal specific error
        return { status: 'error', message: msg };
      }

      console.log(
        `[DataContext] Attempting to updateUserProfile for UID: ${uid}`,
      );
      setUserProfileModalError(null); // Clear modal error before request
      setIsUserProfileModalLoading(true); // Use modal specific loading state

      try {
        const baseUrl =
          process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const token = await currentUser.getIdToken(true); // Ensure fresh token

        // Determine content type based on payload type (FormData vs JSON)
        const headers = { Authorization: `Bearer ${token}` };
        let contentType = 'application/json'; // Default for JSON payload

        if (payload instanceof FormData) {
          // If payload is FormData (contains file), browser sets Content-Type
          // to multipart/form-data automatically, including the boundary.
          // DO NOT manually set 'Content-Type': 'multipart/form-data' here,
          // it will break the request body formatting.
          contentType = undefined; // Let browser handle it
        } else {
          headers['Content-Type'] = 'application/json';
        }

        const response = await axios.put(
          // Using PUT for update
          `${baseUrl}/auth/api/updateUserProfile/${uid}`,
          payload, // Send FormData or JSON payload
          { headers: headers },
        );

        console.log('[DataContext] updateUserProfile success:', response.data);

        // After successful update, re-fetch the user profile to update context state
        await retrieveUserProfile(); // Use the useCallback version

        setIsUserProfileModalLoading(false);
        // Do NOT set main error or profile modal error on success
        return { status: 'success', data: response.data };
      } catch (err) {
        // 'err' is the Firebase error object from above.
        const formattedError = formatFirebaseError(err, 'updateUserProfile'); // <-- Call our complete utility
        setError(formattedError);
        setIsUserProfileModalLoading(false);
        return formattedError;
      }
    },
    [
      currentUser,
      setUserProfileModalError,
      setIsUserProfileModalLoading,
      retrieveUserProfile,
      setError,
    ],
  );

  const fetchComplianceStatus = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiCheckCompliance(token);
      if (result.status === 'success') {
        dispatch({
          type: actionTypes.SET_PENDING_AGREEMENTS,
          payload: result.pending,
        });
        return result.pending;
      }
    } catch (err) {
      console.error('Failed to check compliance:', err);
    }
  }, [currentUser]);

  const acceptLegalAgreement = useCallback(
    async (versionId) => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiAcceptAgreement(token, versionId);
        if (result.status === 'success') {
          // Re-fetch to clear the pending list and unlock the app
          await fetchComplianceStatus();
        }
        return result;
      } catch (err) {
        setError(err);
        return { status: 'error', message: err.message };
      }
    },
    [currentUser, fetchComplianceStatus, setError],
  );

  const fetchSocialMediaConnections = useCallback(
    async (userId) => {
      setLoading(true);
      try {
        if (!currentUser) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const connections = await apiFetchSocialCredentials(token, userId);

        // This is the key change. We now build a detailed object.
        const formattedConnections = {};
        if (Array.isArray(connections)) {
          // Ensure connections is an array
          connections.forEach((conn) => {
            // conn is the full object from the backend:
            // { socialNetwork, accountIdentifier, expired, expiryTime, lastSyncAt }
            if (!formattedConnections[conn.socialNetwork]) {
              formattedConnections[conn.socialNetwork] = {};
            }
            // Store the entire connection object, not just a "connected" string.
            formattedConnections[conn.socialNetwork][conn.accountIdentifier] =
              conn;
          });
        }

        // Update the global state with the new detailed object.
        setSocialMediaConnections({ [userId]: formattedConnections });
      } catch (err) {
        // 'err' is the Firebase error object from above.
        const formattedError = formatFirebaseError(
          err,
          'fetchSocialMediaConnections',
        ); // <-- Call our complete utility
        setError(formattedError);
        throw formattedError;
      } finally {
        setLoading(false);
      }
    },
    [currentUser, setLoading, setError, setSocialMediaConnections],
  );

  // =======================================================================
  // === ANALYTICS DASHBOARD API WRAPPERS (NEW) ===
  // =======================================================================

  const fetchAnalyticsData = useCallback(
    async (visualizationPayload) => {
      if (!currentUser) {
        const errorMsg = 'User not authenticated.';
        console.error('[DataContext] fetchAnalyticsData:', errorMsg);
        // Set the modal-specific error state
        setAnalyticsDashboardError(errorMsg);
        return {
          status: false,
          message: errorMsg,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }

      // Set modal-specific loading and clear previous errors
      setAnalyticsDashboardLoading(true);
      setAnalyticsDashboardError(null);
      console.debug(
        '[DataContext.fetchAnalyticsData] Fetching with payload:',
        visualizationPayload,
      );

      try {
        const token = await currentUser.getIdToken(true); // Ensure a fresh token
        const result = await apiFetchVisualizationData(
          token,
          visualizationPayload,
        );

        if (result.status) {
          console.debug(
            '[DataContext.fetchAnalyticsData] Success, data received:',
            result.data,
          );
          // On success, update the global state with the data from the backend
          setAnalyticsDashboardData(result.data);
          // Also save this successful query for the "last viewed" feature
          setLastAnalyticsQuery(visualizationPayload);
        } else {
          // The API layer returned a structured error { status: false, message, error }
          console.error(
            '[DataContext.fetchAnalyticsData] API Error:',
            result.message,
          );
          // Set the modal-specific error
          setAnalyticsDashboardError(result.message);
          // Clear any previous data on error
          setAnalyticsDashboardData(null);
        }

        // Return the full result object to the calling component for any local handling
        return result;
      } catch (err) {
        // Catch unexpected errors (e.g., from Firebase token refresh, network issues)
        const formattedError = formatFirebaseError(err, 'fetchAnalyticsData');
        console.error(
          '[DataContext.fetchAnalyticsData] Caugh unhandled error:',
          formattedError.message,
        );
        // Set both the global error and the modal-specific error
        setError(formattedError.message);
        setAnalyticsDashboardError(formattedError.message);
        setAnalyticsDashboardData(null); // Clear data on error
        return formattedError;
      } finally {
        // Always turn off the modal-specific loading state
        setAnalyticsDashboardLoading(false);
      }
    },
    [
      currentUser,
      setAnalyticsDashboardLoading,
      setAnalyticsDashboardError,
      setAnalyticsDashboardData,
      setLastAnalyticsQuery,
      setError,
    ],
  );

  const fetchUserInvoices = useCallback(async () => {
    if (!currentUser) return;

    setAnalyticsDashboardLoading(true);
    setAnalyticsDashboardError(null);
    console.debug(
      '[DataContext.fetchUserInvoices] Fetching billing history...',
    );

    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiFetchUserInvoices(token);

      if (result.status) {
        dispatch({ type: actionTypes.SET_USER_INVOICES, payload: result.data });
      } else {
        setAnalyticsDashboardError(result.message);
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchUserInvoices');
      setAnalyticsDashboardError(formattedError.message);
    } finally {
      setAnalyticsDashboardLoading(false);
    }
  }, [currentUser, setAnalyticsDashboardLoading, setAnalyticsDashboardError]);

  // =======================================================================
  // === DISCOVERY PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const importInfluencers = useCallback(
    async (platform, influencersToImport, source) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        // --- START FIX ---
        // The function's ONLY job is to call the API and return the result.
        const result = await apiImportDiscoveredInfluencers(
          token,
          platform,
          influencersToImport,
          source,
        );

        if (result.status && source === 'on_hold') {
          const dbIdsToRemove = influencersToImport.map((inf) => inf.id);
          removeFromOnHoldInfluencers(dbIdsToRemove);
        }
        return result; // Return the raw result to the caller.
        // --- END FIX ---
      } catch (err) {
        // 'err' is the Firebase error object from above.
        const formattedError = formatFirebaseError(err, 'importInfluencers'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
      // The dependency array is now simpler.
    },
    [currentUser, removeFromOnHoldInfluencers, setError],
  );

  const moveInfluencersToHold = useCallback(
    async (influencersToHold) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        // --- START FIX ---
        // Call the API, get the result.
        const result = await apiMoveInfluencersToHold(token, influencersToHold);

        // If successful, update the separate global list (this is fine).
        if (result.status) {
          addToOnHoldInfluencers(result.data.on_hold_influencers);
        }
        return result; // Return the raw result to the caller.
        // --- END FIX ---
      } catch (err) {
        // 'err' is the Firebase error object from above.
        const formattedError = formatFirebaseError(
          err,
          'moveInfluencersToHold',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, addToOnHoldInfluencers, setError],
  );

  const permanentlyDiscardInfluencers = useCallback(
    async (influencersToDiscard) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        // --- START FIX ---
        // Call the API and return its result directly. No more UI logic here.
        const result = await apiPermanentlyDiscardInfluencers(
          token,
          influencersToDiscard,
        );

        // If they came from the on-hold list, still remove them from that separate state.
        if (result.status) {
          const dbIdsToRemove = influencersToDiscard
            .map((inf) => inf.id)
            .filter(Boolean);
          if (dbIdsToRemove.length > 0) {
            removeFromOnHoldInfluencers(dbIdsToRemove);
          }
        }
        return result;
        // --- END FIX ---
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'permanentlyDiscardInfluencers',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, removeFromOnHoldInfluencers, setError],
  );

  const batchRefreshOnHoldInfluencers = useCallback(async () => {
    if (!currentUser) return { status: false, message: 'Not authenticated.' };
    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiBatchRefreshOnHoldInfluencers(token);
      if (result.status) {
        // Dispatch the batch update action with the payload from the backend
        const updatedInfluencers = result.data.updated_influencers;
        if (updatedInfluencers && updatedInfluencers.length > 0) {
          dispatch({
            type: actionTypes.UPDATE_MANY_ON_HOLD_INFLUENCERS,
            payload: updatedInfluencers,
          });
        }
      }
      return result;
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'batchRefreshOnHoldInfluencers',
      ); // <-- Call our complete utility
      setError(formattedError);
      return formattedError;
    }
  }, [currentUser, dispatch, setError]);

  const analyzeOnHoldInfluencer = useCallback(
    async (onHoldId) => {
      if (!currentUser) return { status: false, message: 'Not authenticated.' };
      // This wrapper just calls the API. It does not update state directly.
      // The component calling it will use the returned data to populate the modal.
      try {
        const token = await currentUser.getIdToken(true);
        return await apiAnalyzeOnHoldInfluencer(token, onHoldId);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const singleRefreshOnHoldInfluencer = useCallback(
    async (onHoldId) => {
      if (!currentUser) return { status: false, message: 'Not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiSingleRefreshOnHoldInfluencer(token, onHoldId);
        if (result.status) {
          // On success, dispatch an update for just this one influencer
          // using the existing `UPDATE_ON_HOLD_INFLUENCER` action.
          updateOnHoldInfluencer(result.data);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'singleRefreshOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, updateOnHoldInfluencer, setError],
  ); // Dependency on the dispatcher

  // =======================================================================
  // === DISCOVERY SETTINGS PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchAllCountries = useCallback(async () => {
    if (!currentUser)
      return { status: false, message: 'User not authenticated.' };
    // This function is simple, it just passes through the API call result.
    // The component will handle the data directly.
    try {
      const token = await currentUser.getIdToken(true);
      return await apiFetchAllCountries(token);
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchAllCountries'); // <-- Call our complete utility
      setError(formattedError);
      return formattedError;
    }
  }, [currentUser, setError]);

  const fetchDiscoverySettings = useCallback(async () => {
    if (!currentUser)
      return { status: false, message: 'User not authenticated.' };

    setDiscoverySettingsLoading(true);
    setDiscoverySettingsError(null);
    console.debug('[DataContext.fetchDiscoverySettings] Fetching...');

    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiFetchDiscoverySettings(token);

      if (result.status) {
        setDiscoverySettings(result.data);
        return { status: true, data: result.data };
      } else {
        setDiscoverySettingsError(result.message);
        return { status: false, message: result.message };
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchDiscoverySettings'); // <-- Call our complete utility
      setError(formattedError);
      return formattedError;
    } finally {
      setDiscoverySettingsLoading(false);
    }
  }, [
    currentUser,
    setDiscoverySettingsLoading,
    setDiscoverySettingsError,
    setDiscoverySettings,
    setError,
  ]);

  const updateDiscoverySettings = useCallback(
    async (settingsPayload) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };

      setDiscoverySettingsLoading(true);
      setDiscoverySettingsError(null);
      console.debug(
        '[DataContext.updateDiscoverySettings] Updating with payload:',
        settingsPayload,
      );

      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiUpdateDiscoverySettings(token, settingsPayload);

        if (result.status) {
          // On successful update, re-fetch the settings to ensure state is in sync
          await fetchDiscoverySettings();
          return { status: true, message: result.message };
        } else {
          setDiscoverySettingsError(result.message);
          return { status: false, message: result.message };
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'updateDiscoverySettings',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setDiscoverySettingsLoading(false);
      }
    },
    [
      currentUser,
      fetchDiscoverySettings,
      setDiscoverySettingsLoading,
      setDiscoverySettingsError,
      setError,
    ],
  );

  const generateDiscoveryKeywords = useCallback(
    async (numberOfKeywords) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };

      console.debug(
        '[DataContext.generateDiscoveryKeywords] Triggering AI generation...',
      );

      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiGenerateDiscoveryKeywords(
          token,
          numberOfKeywords,
        );

        // We don't set global state here. The modal handles the data locally
        // so the user can review the keywords in the textarea before saving.
        if (!result.status) {
          // We can leverage the existing modal error state for immediate feedback
          setDiscoverySettingsError(result.message);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'generateDiscoveryKeywords',
        );
        setError(formattedError);
        setDiscoverySettingsError(formattedError.message);
        return { status: false, message: formattedError.message };
      }
    },
    [currentUser, setDiscoverySettingsError, setError],
  );

  // =======================================================================
  // === INFLUENCER PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  // UPDATED WRAPPER FOR THE AGGREGATED ENDPOINT
  const fetchInfluencerPageData = useCallback(
    async (influencerId) => {
      if (!currentUser) {
        const msg = 'User not authenticated.';
        setError(msg); // Set global error state
        return { status: false, message: msg }; // Return consistent error object
      }

      try {
        const token = await currentUser.getIdToken(true);

        // Call the clean API function from InfluencerPageApi.js
        const result = await apiFetchInfluencerPageData(token, influencerId);

        // The API function already returns a structured object { status, data?, message? }.
        // We can check the status here if we need to perform context-specific actions.
        if (!result.status) {
          // If the API call failed, set the global error state with the message from the API layer.
          setError(
            result.message ||
              'An unknown error occurred while fetching page data.',
          );
        }

        // Return the result directly to the calling component (InfluencerPage.js)
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchInfluencerPageData',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const fetchInfluencerData = useCallback(
    async (influencerId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/user/api/influencerData/${influencerId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        return [response.data]; // transforms object into array expected for queryResults
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'fetchInfluencerData'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  ); // Add setError to dependency array

  // Create a callback function to fetch products for a given influencerId.
  const fetchProductsUsed = useCallback(
    async (influencerId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/user/api/getProducts/${influencerId}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: function (status) {
            return status >= 200 && status < 400;
          },
        });
        if (response.status === 304) {
          console.log(
            'Received 304 Not Modified response for products:',
            influencerId,
          );
          return [];
        }
        return response.data;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'fetchProductsUsed'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const updateProducts = useCallback(
    async (influencerId, products) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        const payload = { influencerId, products };
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/user/api/updateProducts`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        return response.data;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updateProducts'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const fetchSocialMedias = useCallback(
    async (influencerId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiFetchSocialMedias(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'fetchSocialMedias'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const fetchSocialContacts = useCallback(
    async (influencerId) => {
      const auth = getAuth(); // If using Firebase auth
      const user = auth.currentUser; // If using Firebase auth

      if (!user) {
        // currentUser should be available from your auth state
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        console.error(
          '[DataContext - fetchSocialContacts] User not authenticated.',
        );
        return {
          status: false,
          message: ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED],
        }; // Return an object similar to API responses
      }
      try {
        const token = await currentUser.getIdToken(true); // Or your method for getting token
        console.debug(
          '[DataContext - fetchSocialContacts] Fetching social contacts for influencerId:',
          influencerId,
        );
        const result = await apiFetchSocialContacts(token, influencerId);
        if (!result.status) {
          // The API wrapper already formats the error, DataContext can choose to set global error or let component handle
          setError(
            result.message || ERROR_MESSAGES[ERROR_CODES.INTERNAL_SERVER_ERROR],
          );
          console.error(
            '[DataContext - fetchSocialContacts] Error from apiFetchSocialContacts:',
            result.message,
          );
        }
        return result; // Return the full result object { status, data?, error?, message? }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'fetchSocialContacts'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  ); // Add currentUser and setError to dependencies

  // --- NEW: API WRAPPER TO ADD A SOCIAL CONTACT ---
  const addSocialContact = useCallback(
    async (contactData) => {
      if (!currentUser) {
        const errorMsg = 'Authentication error: No user is signed in.';
        console.error('[DataContext - addSocialContact]', errorMsg);
        setError(errorMsg);
        // Return the standard failure object format
        return {
          status: false,
          message: errorMsg,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }
      try {
        const token = await currentUser.getIdToken(true);
        console.debug(
          '[DataContext - addSocialContact] Calling API to add social contact:',
          contactData,
        );
        // The result from the API layer is already in the { status, ... } format
        const result = await apiAddSocialContact(token, contactData);

        // The component will handle displaying success/error messages based on the result
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'addSocialContact'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO UPDATE A SOCIAL CONTACT ---
  const updateSocialContact = useCallback(
    async (contactId, updates) => {
      if (!currentUser) {
        const errorMsg = 'Authentication error: No user is signed in.';
        setError(errorMsg);
        return {
          status: false,
          message: errorMsg,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }
      try {
        const token = await currentUser.getIdToken(true);
        return await apiUpdateSocialContact(token, contactId, updates);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updateSocialContact'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO ADD A SOCIAL MEDIA (PUBLISHING CHANNEL) ---
  const addSocialMedia = useCallback(
    async (mediaData) => {
      if (!currentUser) {
        const errorMsg = 'Authentication error: No user is signed in.';
        console.error('[DataContext - addSocialMedia]', errorMsg);
        setError(errorMsg);
        return {
          status: false,
          message: errorMsg,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }
      try {
        const token = await currentUser.getIdToken(true);
        console.debug(
          '[DataContext - addSocialMedia] Calling API to add publishing channel:',
          mediaData,
        );
        const result = await apiAddSocialMedia(token, mediaData);

        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'addSocialMedia'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO UPDATE A SOCIAL MEDIA (PUBLISHING CHANNEL) ---
  const updateSocialMedia = useCallback(
    async (mediaId, updates) => {
      if (!currentUser) {
        const errorMsg = 'Authentication error: No user is signed in.';
        setError(errorMsg);
        return {
          status: false,
          message: errorMsg,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }
      try {
        const token = await currentUser.getIdToken(true);
        return await apiUpdateSocialMedia(token, mediaId, updates);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updateSocialMedia'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO UPDATE PARTNER INFO ---
  const updatePartnerInfo = useCallback(
    async (influencerId, updates) => {
      if (!currentUser) {
        const errorMsg = 'Authentication error: No user is signed in.';
        setError(errorMsg);
        return {
          status: false,
          message: errorMsg,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }
      try {
        const token = await currentUser.getIdToken(true);
        console.debug(
          '[DataContext - updatePartnerInfo] Calling API to update partner info for influencer:',
          influencerId,
        );
        const result = await apiUpdatePartnerInfo(token, influencerId, updates);
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updatePartnerInfo'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO ADD A NEW INFLUENCER ---
  const addNewInfluencer = useCallback(
    async (influencerData) => {
      if (!currentUser) {
        setError('Authentication error: No user is signed in.');
        return { status: false, message: 'Not authenticated.' };
      }
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiAddNewInfluencer(token, influencerData);
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'addNewInfluencer'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO FETCH DETAILS FOR A SINGLE INFLUENCER ---
  const getInfluencerDetails = useCallback(
    async (influencerId) => {
      if (!currentUser) {
        const errorMsg = 'Authentication error: No user is signed in.';
        setError(errorMsg);
        return { status: false, message: errorMsg };
      }
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiGetInfluencerDetails(token, influencerId);
        return result; // The result is already in the { status, data?, message? } format
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'getInfluencerDetails'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW: API WRAPPER TO DELETE AN INFLUENCER ---
  const deleteInfluencer = useCallback(
    async (influencerId) => {
      if (!currentUser) {
        setError('Authentication error: No user is signed in.');
        return { status: false, message: 'Not authenticated.' };
      }
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiDeleteInfluencer(token, influencerId);
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'deleteInfluencer'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW API WRAPPERS FOR INFLUENCER HEALTH ANALYSIS ---
  const refreshInfluencerHealth = useCallback(
    async (influencerId, socialMediaId) => {
      if (!currentUser) {
        return { status: false, message: 'Not authenticated.' };
      }
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiRefreshInfluencerHealth(
          token,
          influencerId,
          socialMediaId,
        );

        if (result.status) {
          // SUCCESS: Refetch the main influencer data to update the UI globally.
          // This ensures consistency across the entire app.
          console.debug(
            `[DataContext] Health refreshed for ${influencerId}, refetching query data.`,
          );
          const updatedDetails = await apiGetInfluencerDetails(
            token,
            influencerId,
          );
          if (updatedDetails.status) {
            // Use the functional update form of setQueryResults to safely update state
            setQueryResults((prevResults) =>
              prevResults.map((inf) =>
                inf.influencerId === influencerId ? updatedDetails.data : inf,
              ),
            );
          }
        } else {
          if (
            result &&
            !result.status &&
            result.error.code === ERROR_CODES.OAUTH_TOKEN_REVOKED
          ) {
            console.warn(
              `[DataContext] OAUTH_TOKEN_REVOKED detected. Forcing state re-sync.`,
            );
            await fetchSocialMediaConnections(currentUser.uid);
            setError(result.error);
          }
        }
        return result; // Return the original result to the component
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'refreshInfluencerHealth',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError, setQueryResults, fetchSocialMediaConnections],
  );

  const fetchInfluencerHealthDashboard = useCallback(
    async (influencerId) => {
      if (!currentUser) {
        return { status: false, message: 'Not authenticated.' };
      }
      try {
        const token = await currentUser.getIdToken(true);
        // This just calls the API and returns the data for the modal.
        // It does not modify global state.
        return await apiFetchInfluencerHealthDashboard(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchInfluencerHealthDashboard',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW API WRAPPERS FOR CONTENT FOCUS ---
  const refreshInfluencerContentFocus = useCallback(
    async (influencerId, socialMediaId) => {
      if (!currentUser) return { status: false, message: 'Not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiRefreshInfluencerContentFocus(
          token,
          influencerId,
          socialMediaId,
        );
        if (result.status) {
          // On success, refetch the main influencer data to update the UI
          const updatedDetails = await apiGetInfluencerDetails(
            token,
            influencerId,
          );
          if (updatedDetails.status) {
            setQueryResults((prev) =>
              prev.map((inf) =>
                inf.influencerId === influencerId ? updatedDetails.data : inf,
              ),
            );
          }
        } else {
          if (
            result &&
            !result.status &&
            result.error.code === ERROR_CODES.OAUTH_TOKEN_REVOKED
          ) {
            console.warn(
              `[DataContext] OAUTH_TOKEN_REVOKED detected. Forcing state re-sync.`,
            );
            await fetchSocialMediaConnections(currentUser.uid);
            setError(result.error);
          }
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'refreshInfluencerContentFocus',
        );
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError, setQueryResults, fetchSocialMediaConnections],
  );

  const fetchInfluencerContentFocusDashboard = useCallback(
    async (influencerId, socialMediaId) => {
      if (!currentUser) return { status: false, message: 'Not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiFetchInfluencerContentFocusDashboard(
          token,
          influencerId,
          socialMediaId,
        );
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchInfluencerContentFocusDashboard',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // =======================================================================
  // === QUERY PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================
  const runSingleQuery = useCallback(
    async (queryParams) => {
      setLoading(true);
      try {
        if (!currentUser) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const data = await fetchSingleQuery(token, queryParams);
        return data;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'runSingleQuery'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, currentUser, setError],
  );

  const runMultipleQuery = useCallback(
    async (queryParams) => {
      setLoading(true);
      try {
        if (!currentUser) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const data = await fetchMultipleQuery(token, queryParams);
        return data;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'runMultipleQuery'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, currentUser, setError],
  );

  // =======================================================================
  // === PUBLICATION PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchPublicationsByInfluencer = useCallback(
    async (influencerId) => {
      if (!currentUser)
        return {
          status: false,
          message: 'User not authenticated.',
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiFetchPublicationsByInfluencer(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchPublicationsByInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const fetchProductsToPublish = useCallback(
    async (influencerId) => {
      if (!currentUser)
        return {
          status: false,
          message: 'User not authenticated.',
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiFetchProductsToPublish(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchProductsToPublish',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const createPublication = useCallback(
    async (payload) => {
      if (!currentUser)
        return {
          status: false,
          message: 'User not authenticated.',
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiCreatePublication(token, payload);

        // CORRECT, ROBUST ERROR HANDLING
        if (
          result &&
          !result.status &&
          result.error.code === ERROR_CODES.OAUTH_TOKEN_REVOKED
        ) {
          console.warn(
            `[DataContext] OAUTH_TOKEN_REVOKED detected for channel: ${payload.channel}. Forcing state re-sync.`,
          );
          await fetchSocialMediaConnections(currentUser.uid);
          setError(result.error);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'createPublication'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, fetchSocialMediaConnections, setError],
  );

  const deletePublication = useCallback(
    async (publicationId) => {
      // This is a pure database operation, so it doesn't need the token logic.
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiDeletePublication(token, publicationId);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'deletePublication'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- Wrappers with UI State Orchestration ---

  const togglePublishedProduct = useCallback(
    async (publicationId, productId) => {
      if (!currentUser)
        return {
          status: false,
          message: 'User not authenticated.',
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };

      // Orchestration Step 1: Set immediate UI loading state
      setPublicationTogglingProduct(publicationId, productId, true);

      try {
        const token = await currentUser.getIdToken(true);
        const response = await apiTogglePublishedProduct(
          token,
          publicationId,
          productId,
        );

        // Orchestration Step 2: On success, update the global state with the new data from the API
        if (response.status) {
          updatePublicationProducts(
            publicationId,
            response.data.updatedProducts,
          );
        }

        // Always return the original response for the component to use
        return response;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'togglePublishedProduct',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        // Orchestration Step 3: Always turn off the loading indicator
        setPublicationTogglingProduct(publicationId, productId, false);
      }
    },
    [
      currentUser,
      setPublicationTogglingProduct,
      updatePublicationProducts,
      setError,
    ],
  );

  const toggleIncludeVideoData = useCallback(
    async (publicationId) => {
      if (!currentUser)
        return {
          status: false,
          message: 'User not authenticated.',
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };

      setPublicationTogglingVideo(publicationId, true);
      try {
        const token = await currentUser.getIdToken(true);
        const response = await apiToggleIncludeVideoData(token, publicationId);
        if (response.status) {
          updatePublicationVideoData(
            publicationId,
            response.data.includeVideoData,
          );
        }
        return response;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'toggleIncludeVideoData',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setPublicationTogglingVideo(publicationId, false);
      }
    },
    [
      currentUser,
      setPublicationTogglingVideo,
      updatePublicationVideoData,
      setError,
    ],
  );

  const addPublishedProduct = useCallback(
    async (publicationId, productId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };

      // Orchestration: Set loading state for the specific product being actioned.
      setPublicationTogglingProduct(publicationId, productId, true);

      try {
        const token = await currentUser.getIdToken(true);
        const response = await apiAddPublishedProduct(
          token,
          publicationId,
          productId,
        );

        // On success, update the global state with the new, complete list of products from the API.
        if (response.status) {
          updatePublicationProducts(
            publicationId,
            response.data.updatedProducts,
          );
        }

        return response;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'addPublishedProduct'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        // Always turn off the loading indicator for that specific product.
        setPublicationTogglingProduct(publicationId, productId, false);
      }
    },
    [
      currentUser,
      setPublicationTogglingProduct,
      updatePublicationProducts,
      setError,
    ],
  );

  const deletePublishedProduct = useCallback(
    async (publicationId, productId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };

      // CORRECTED: Sets the loading state for the specific product being actioned.
      setPublicationTogglingProduct(publicationId, productId, true);

      try {
        const token = await currentUser.getIdToken(true);
        const response = await apiDeletePublishedProduct(
          token,
          publicationId,
          productId,
        );

        // On success, update the global state with the new, complete list of products from the API.
        if (response.status) {
          // Assumes the backend returns the updated list of products in response.data.updatedProducts
          updatePublicationProducts(
            publicationId,
            response.data.updatedProducts,
          );
        }

        return response;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'deletePublishedProduct',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        // Always turn off the loading indicator for that specific product.
        setPublicationTogglingProduct(publicationId, productId, false);
      }
    },
    [
      currentUser,
      setPublicationTogglingProduct,
      updatePublicationProducts,
      setError,
    ],
  );

  const refreshPublicationMetadata = useCallback(
    async (influencerId, publicationId) => {
      if (!currentUser) return { status: false, message: 'Not authenticated.' };

      // Set loading state for this specific publication
      setPublicationRefreshingMetadata(publicationId, true);

      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiRefreshPublicationMetadata(
          token,
          influencerId,
          publicationId,
        );

        if (result.status) {
          // On success, the backend returns the full updated publication object.
          // Dispatch an action to replace the old object in the state.
          updatePublicationDetails(result.data);
        }

        // Return the raw result to the component for toast notifications
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'refreshPublicationMetadata',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        // Always turn off the loading indicator
        setPublicationRefreshingMetadata(publicationId, false);
      }
    },
    [
      currentUser,
      setError,
      setPublicationRefreshingMetadata,
      updatePublicationDetails,
    ],
  );

  const fetchPublicationAiSummary = useCallback(
    async (publicationId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiFetchPublicationAiSummary(token, publicationId);
        if (result.status) {
          setPublicationAiSummary(publicationId, result.data.aiSummary);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchPublicationAiSummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setPublicationAiSummary, setError],
  );

  const updatePublicationAISummary = useCallback(
    async (publicationId, newSummary) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiUpdatePublicationAISummary(
          token,
          publicationId,
          newSummary,
        );
        if (result.status) {
          setPublicationAiSummary(publicationId, newSummary);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'updatePublicationAISummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setPublicationAiSummary, setError],
  );

  // --- Market Fit & Aggregated Analysis (Simple Pass-Through Wrappers) ---

  const fetchAggregatedMarketFit = useCallback(
    async (influencerId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiFetchAggregatedMarketFit(token, influencerId);

        // *** THIS IS THE FIX ***
        // On success, dispatch the result to the global state. On failure, dispatch null to clear it.
        dispatch({
          type: actionTypes.SET_AGGREGATED_MARKET_FIT,
          payload: result.status ? result.data : null,
        });

        // Always return the original result for the component to inspect if needed.
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchAggregatedMarketFit',
        ); // <-- Call our complete utility
        setError(formattedError);
        dispatch({
          type: actionTypes.SET_AGGREGATED_MARKET_FIT,
          payload: null,
        });
        return formattedError;
      }
    },
    [currentUser, dispatch, setError],
  ); // <-- Add `dispatch` to the dependency array

  const fetchAggregatedPublicationSummary = useCallback(
    async (influencerId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiFetchAggregatedPublicationSummary(
          token,
          influencerId,
        );
        console.log(
          '[DataContex] Aggregated Publication Summary before Dispatch:',
          result,
        );
        // *** THIS IS THE FIX ***
        // On success, dispatch the result to the global state. On failure, dispatch null to clear it.
        dispatch({
          type: actionTypes.SET_AGGREGATED_PUBLICATION_SUMMARY,
          payload: result.status ? result.data : null,
        });

        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchAggregatedPublicationSummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        dispatch({
          type: actionTypes.SET_AGGREGATED_PUBLICATION_SUMMARY,
          payload: null,
        });
        return formattedError;
      }
    },
    [currentUser, dispatch, setError],
  ); // <-- Add `dispatch` to the dependency array

  const fetchIndividualMarketFitAnalysis = useCallback(
    async (publication) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiFetchIndividualMarketFitAnalysis(
          token,
          publication.publicationId,
        );
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchIndividualMarketFitAnalysis',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // functions that need Token expiration management
  const generateSingleMarketFitAnalysis = useCallback(
    async (params) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiGenerateSingleMarketFitAnalysis(token, params);

        // ADDED: Replicated token expiration logic
        if (
          result &&
          !result.status &&
          result.error === ERROR_CODES.OAUTH_TOKEN_REVOKED
        ) {
          console.warn(
            `[DataContext] OAUTH_TOKEN_REVOKED during single market fit analysis. Forcing state re-sync.`,
          );
          await fetchSocialMediaConnections(currentUser.uid);
          setReconnectNetwork('youtube');
        }

        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'generateSingleMarketFitAnalysis',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, fetchSocialMediaConnections, setReconnectNetwork, setError],
  );

  const generatePublicationAISummary = useCallback(
    async (params) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      setPublicationGeneratingSummary(params.publicationId, true);
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiGeneratePublicationAISummary(token, params);

        // ADDED: Replicated token expiration logic
        if (
          result &&
          !result.status &&
          result.error === ERROR_CODES.OAUTH_TOKEN_REVOKED
        ) {
          console.warn(
            `[DataContext] OAUTH_TOKEN_REVOKED during summary generation. Forcing state re-sync.`,
          );
          await fetchSocialMediaConnections(currentUser.uid);
          // We don't know the exact channel key here, so we trigger a generic reconnect prompt
          // The ErrorDisplayModal will guide them to the settings page.
          setReconnectNetwork('youtube'); // A generic hint
        } else if (result.status) {
          setPublicationAiSummary(params.publicationId, result.data.aiSummary);
        }

        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'generatePublicationAISummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setPublicationGeneratingSummary(params.publicationId, false);
      }
    },
    [
      currentUser,
      fetchSocialMediaConnections,
      setReconnectNetwork,
      setPublicationGeneratingSummary,
      setPublicationAiSummary,
      setError,
    ],
  );

  const generatePrivatePublicationAISummary = useCallback(
    async (params) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      setPublicationGeneratingSummary(params.publicationId, true);
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiGeneratePrivatePublicationAISummary(
          token,
          params,
        );

        // ADDED: Replicated token expiration logic
        if (
          result &&
          !result.status &&
          result.error === ERROR_CODES.OAUTH_TOKEN_REVOKED
        ) {
          console.warn(
            `[DataContext] OAUTH_TOKEN_REVOKED during private summary generation. Forcing state re-sync.`,
          );
          await fetchSocialMediaConnections(currentUser.uid);
          setReconnectNetwork('youtube_private');
        } else if (result.status) {
          setPublicationAiSummary(params.publicationId, result.data.aiSummary);
        }

        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'generatePrivatePublicationAISummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setPublicationGeneratingSummary(params.publicationId, false);
      }
    },
    [
      currentUser,
      fetchSocialMediaConnections,
      setReconnectNetwork,
      setPublicationGeneratingSummary,
      setPublicationAiSummary,
      setError,
    ],
  );

  // =======================================================================
  // === KNOWLEDGE PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const generateAISummary = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          // Create a structured error and set it globally
          const authError = {
            code: ERROR_CODES.USER_NOT_AUTHENTICATED,
            message: ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED],
          };
          setError(authError);
          return authError; // Return the error object
        }
        const token = await user.getIdToken(true);

        const result = await apiGenerateAISummary(token, params);

        // --- MODIFICATION: Handle structured errors from the API response ---
        if (result && result.error) {
          console.error(
            '[DataContext.generateAISummary] API Error:',
            result.message,
          );
          // The API layer already returns a structured error. Pass it to the global state.
          const apiError = { code: result.error, message: result.message };
          setError(apiError);
          return apiError;
        }

        // On success, the API returns the summary string directly.
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'generateAISummary'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  ); // Add setError to dependency array

  const fetchInfluencerAiSummary = useCallback(
    async (influencerId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        return await apiFetchInfluencerAiSummary(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchInfluencerAiSummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const updateAISummary = useCallback(
    async (influencerId, newSummary) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);

        return await apiUpdateAISummary(token, influencerId, newSummary);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updateAISummary'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  // =======================================================================
  // === RELATIONSHIP PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const generateMessagesSummary = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          // Return a consistent error object format
          return {
            status: 'error',
            error: ERROR_CODES.USER_NOT_AUTHENTICATED,
            message: 'User not authenticated.',
          };
        }
        const token = await user.getIdToken(true);

        // Call the API layer function
        const result = await apiGenerateMessagesSummary(token, params);

        // --- THE FIX IS HERE ---
        // After getting the result, check if it contains the specific token revoked error.
        if (result && result.errors && Array.isArray(result.errors)) {
          const reconnectError = result.errors.find(
            (err) => err.code === ERROR_CODES.OAUTH_TOKEN_REVOKED,
          );

          if (reconnectError) {
            console.warn(
              '[DataContext] OAUTH_TOKEN_REVOKED detected. Setting this specific error for the modal.',
            );
            // Re-fetch connection statuses to update UI indicators (e.g., show "Disconnected").
            await fetchSocialMediaConnections(currentUser.uid);

            // Set the SPECIFIC error object. The ErrorDisplayModal will now
            // immediately see the code it needs to show the "Go to Settings" button.
            setError(reconnectError);
          }
        }
        // --- END OF FIX ---

        return result; // Return the original result to the calling component
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'generateMessagesSummary',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError, fetchSocialMediaConnections],
  );

  const fetchMessagesSummary = useCallback(
    async (influencerId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);

        return await apiFetchMessagesSummary(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'fetchMessagesSummary'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const saveMessagesSummary = useCallback(
    async (influencerId, summaryText) => {
      try {
        if (!currentUser) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/messages/api/updateMessageSummary`;
        const payload = { influencerId, summaryText };
        await axios.post(url, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'saveMessagesSummary'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const connectGoogle = useCallback(
    async (params) => {
      try {
        if (!currentUser) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiConnectGoogle(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'connectGoogle'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const disconnectGoogle = useCallback(
    async (params) => {
      try {
        if (!currentUser) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiDisconnectGoogle(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'disconnectGoogle'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // --- NEW YOUTUBE API WRAPPERS (Mirrored from Google) ---

  const connectYouTube = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const result = await apiConnectYouTube(token, params);
        // Re-fetch all connections on success to update the entire state
        if (result.status) {
          await fetchSocialMediaConnections(currentUser.uid);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'connectYouTube'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError, fetchSocialMediaConnections],
  );

  // --- NEW MICROSOFT 365 API WRAPPERS ---

  const connectMicrosoft365 = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        const result = await apiConnectMicrosoft365(token, params);
        // Re-fetch all connections on success to update the entire state
        if (result.status) {
          await fetchSocialMediaConnections(user.uid);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'connectMicrosoft365');
        setError(formattedError);
        return formattedError;
      }
    },
    [setError, fetchSocialMediaConnections],
  );

  const disconnectMicrosoft365 = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        const result = await apiDisconnectMicrosoft365(token, params);
        // Re-fetch all connections on success to update the entire state
        if (result.status) {
          await fetchSocialMediaConnections(user.uid);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'disconnectMicrosoft365',
        );
        setError(formattedError);
        return formattedError;
      }
    },
    [setError, fetchSocialMediaConnections],
  );

  const refreshMicrosoft365Token = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await user.getIdToken(true);
        const result = await apiRefreshMicrosoft365Token(token, params);
        // Re-fetch all connections after a refresh attempt to get the latest status
        await fetchSocialMediaConnections(user.uid);
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'refreshMicrosoft365Token',
        );
        setError(formattedError);
        setReconnectNetwork('microsoft365');
        return formattedError;
      }
    },
    [setError, fetchSocialMediaConnections, setReconnectNetwork],
  );

  const getMicrosoft365Status = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        return await apiGetMicrosoft365Status(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'getMicrosoft365Status',
        );
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const disconnectYouTube = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const result = await apiDisconnectYouTube(token, params);
        // Re-fetch all connections on success to update the entire state
        if (result.status) {
          await fetchSocialMediaConnections(currentUser.uid);
        }
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'disconnectYouTube'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError, fetchSocialMediaConnections],
  );

  const refreshYouTubeToken = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await currentUser.getIdToken(true);
        const result = await apiRefreshYouTubeToken(token, params);
        // Re-fetch all connections after a refresh attempt to get the latest status
        await fetchSocialMediaConnections(currentUser.uid);
        return result;
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'refreshYouTubeToken'); // <-- Call our complete utility
        setError(formattedError);
        setReconnectNetwork('youtube');
        return formattedError;
      }
    },
    [currentUser, setError, fetchSocialMediaConnections, setReconnectNetwork],
  );

  const connectMeta = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiConnectMeta(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'connectMeta'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const disconnectMeta = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiDisconnectMeta(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'disconnectMeta'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const initTelegramAuth = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiInitTelegramAuth(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'initTelegramAuth'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const completeTelegramAuth = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiCompleteTelegramAuth(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'completeTelegramAuth'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const completeTelegram2FAAuth = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiCompleteTelegram2FAAuth(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'completeTelegram2FAAuth',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const connectTelegram = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiConnectTelegram(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'connectTelegram'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const disconnectTelegram = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiDisconnectTelegram(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'disconnectTelegram'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const listGoogleAccounts = useCallback(async () => {
    try {
      const auth = getAuth(); // Get auth inside
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      const token = await currentUser.getIdToken(true);
      return await apiListGoogleAccounts(token);
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'listGoogleAccounts'); // <-- Call our complete utility
      setError(formattedError);
      return formattedError;
    }
  }, [currentUser, setError]);

  const listMetaAccounts = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiListMetaAccounts(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'listMetaAccounts'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const listTelegramAccounts = useCallback(async () => {
    try {
      const auth = getAuth(); // Get auth inside
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      const token = await currentUser.getIdToken(true);
      return await apiListTelegramAccounts(token);
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'listTelegramAccounts'); // <-- Call our complete utility
      setError(formattedError);
      return formattedError;
    }
  }, [currentUser, setError]);

  const getGoogleStatus = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiGetGoogleStatus(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'getGoogleStatus'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const getMetaStatus = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiGetMetaStatus(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'getMetaStatus'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const getTelegramStatus = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        return await apiGetTelegramStatus(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'getTelegramStatus'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const refreshGoogleToken = useCallback(
    async (params) => {
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await currentUser.getIdToken(true);
        return await apiRefreshGoogleToken(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'refreshGoogleToken'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  const refreshMetaToken = useCallback(
    async (params) => {
      //Add refresh for meta
      try {
        const auth = getAuth(); // Get auth inside
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await currentUser.getIdToken(true);
        return await apiRefreshMetaToken(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'refreshMetaToken'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // =======================================================================
  // === EMAIL PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchNewEmails = useCallback(
    async (params) => {
      // Expected params from EmailPage.js:
      // {
      //   influencerId: 123,
      //   socialNetwork: 'gmail_1', // The abstract ID for the backend API
      //   accountIdentifier: 'user.account1@gmail.com' // The REAL ID for token refresh
      // }
      console.debug('[fetchNewEmails Context] Initiated with params:', params);

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return {
          status: false,
          message: ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED],
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
        };
      }

      try {
        const token = await user.getIdToken(true);

        // This function should only be called for Gmail, as other platforms have different fetch logic.
        // We check if the abstract socialNetwork ID starts with "gmail".
        if (params.socialNetwork && params.socialNetwork.startsWith('gmail')) {
          // The real email address is needed for the pre-emptive token refresh.
          const realAccountId = params.accountIdentifier;
          if (!realAccountId) {
            const errorMessage =
              'No real Gmail account identifier was provided for the token refresh step.';
            console.error(`[fetchNewEmails Context] Error: ${errorMessage}`);
            setError(errorMessage);
            return {
              status: false,
              message: errorMessage,
              error: ERROR_CODES.INVALID_INPUT,
            };
          }

          console.debug(
            `[fetchNewEmails Context] Attempting pre-emptive token refresh for: ${realAccountId}`,
          );
          const refreshResult = await refreshGoogleToken({
            accountId: realAccountId,
          });

          // --- Handle different outcomes from the token refresh attempt ---
          if (refreshResult && refreshResult.error) {
            // UNRECOVERABLE ERROR: The refresh token is missing from the DB.
            // The user MUST re-authenticate completely to get a new one.
            if (refreshResult.error === ERROR_CODES.MISSING_GMAIL_TOKENS) {
              console.error(
                `[fetchNewEmails Context] UNRECOVERABLE ERROR: No refresh token for ${realAccountId}. User must re-authenticate.`,
              );
              setError(ERROR_MESSAGES[refreshResult.error]); // Show specific error message.
              setReconnectNetwork('gmail'); // Signal the UI to show the "Reconnect" prompt.
              // IMPORTANT: We DO NOT call fetchSocialMediaConnections here. We return immediately to break the loop.
              return {
                status: false,
                message: ERROR_MESSAGES[refreshResult.error],
                error: refreshResult.error,
              };
            }

            // RECOVERABLE ERROR: The token is expired, but a refresh token exists.
            // Refreshing the connection list is the correct action to update the UI.
            if (refreshResult.error === ERROR_CODES.GMAIL_TOKEN_EXPIRED) {
              console.warn(
                `[fetchNewEmails Context] Token for ${realAccountId} is expired. Refreshing connection state to update UI.`,
              );
              await fetchSocialMediaConnections(user.uid);
              setError(ERROR_MESSAGES[refreshResult.error]);
              return {
                status: false,
                message: ERROR_MESSAGES[refreshResult.error],
                error: refreshResult.error,
              };
            }

            // OTHER REFRESH ERRORS: Treat as unrecoverable and prompt for reconnect.
            const errorMessage = `Failed to refresh token for ${realAccountId}. Please try reconnecting.`;
            console.error(
              `[fetchNewEmails Context] Unexpected refresh error for ${realAccountId}:`,
              refreshResult,
            );
            setError(errorMessage);
            setReconnectNetwork('gmail');
            return {
              status: false,
              message: errorMessage,
              error: refreshResult.error,
            };
          }

          console.debug(
            `[fetchNewEmails Context] Pre-emptive refresh successful for ${realAccountId}. Now calling the API layer to fetch emails.`,
          );

          // --- Call the API layer (EmailPageApi.js) ---
          // This call uses params.socialNetwork ('gmail_1' or 'gmail_2') to route to the correct backend endpoint.
          const result = await apiFetchNewEmails(token, params);

          // Handle a token expiry error that might happen during the *actual* API call,
          // even if the pre-emptive refresh succeeded.
          if (result && result.error === ERROR_CODES.GMAIL_TOKEN_EXPIRED) {
            console.warn(
              `[fetchNewEmails Context] Token for ${realAccountId} expired during main API call. Refreshing connection state.`,
            );
            await fetchSocialMediaConnections(user.uid);
            setError(ERROR_MESSAGES[result.error]);
            return {
              status: false,
              message: ERROR_MESSAGES[result.error],
              error: result.error,
            };
          } else if (result && result.error) {
            // Handle other structured errors from the API call.
            console.error(
              `[fetchNewEmails Context] API error during email fetch:`,
              result,
            );
            setError(formatApiError(result));
            return result;
          }

          // --- Success Path ---
          return result;
        } else {
          // This block handles non-Gmail calls if this function were ever used for them.
          // It just passes them through to the API layer.
          console.log(
            `[fetchNewEmails Context] Passing call for non-Gmail network (${params.socialNetwork}) to API layer.`,
          );
          return await apiFetchNewEmails(token, params);
        }
      } catch (err) {
        // Catch any unexpected client-side exceptions (e.g., network down).
        const formattedError = formatFirebaseError(err, 'fetchNewEmails'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [
      setError,
      refreshGoogleToken,
      fetchSocialMediaConnections,
      setReconnectNetwork,
    ],
  );

  const generateEmailDraft = useCallback(
    async (params) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        const token = await user.getIdToken(true);
        return await apiGenerateEmailDraft(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'generateEmailDraft'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const translateEmailDraft = useCallback(
    async (influencerId, activeChannel, draft, translationIdiom, country) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        const token = await user.getIdToken(true);
        return await apiTranslateEmailDraft(
          token,
          influencerId,
          activeChannel,
          draft,
          translationIdiom,
          country,
        );
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'translateEmailDraft'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const simpleTranslation = useCallback(
    async (influencerId, emailData) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        const token = await user.getIdToken(true);
        return await apiSimpleTranslation(token, influencerId, emailData);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'simpleTranslation'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const updateActiveChannel = useCallback(
    async (influencerId, activeChannel) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        const token = await user.getIdToken(true);
        return await apiUpdateActiveChannel(token, influencerId, activeChannel);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updateActiveChannel'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const updateInfluencerStatus = useCallback(
    async (influencerId, newInfluencerStatus) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        const token = await user.getIdToken(true);
        return await apiUpdateInfluencerStatus(
          token,
          influencerId,
          newInfluencerStatus,
        );
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'updateInfluencerStatus',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const sendEmail = useCallback(
    async (params) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        const token = await user.getIdToken(true);
        return await apiSendEmail(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'sendEmail'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const obtainNewPartnerData = useCallback(
    async (params) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return { status: false, message: 'Not authenticated' };
      }
      try {
        const token = await user.getIdToken(true);
        // The API wrapper already handles error formatting
        return await apiObtainNewPartnerData(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'obtainNewPartnerData'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const saveConfirmedPartnerData = useCallback(
    async (influencerId, finalData) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return { status: false, message: 'Not authenticated' };
      }
      try {
        const token = await user.getIdToken(true);
        return await apiSaveConfirmedPartnerData(
          token,
          influencerId,
          finalData,
        );
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'saveConfirmedPartnerData',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  // =======================================================================
  // === LLM CONFIGURATION PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchLlmConfigurations = useCallback(async () => {
    console.debug('[DataContext.fetchLlmConfigurations] Fetching...');
    // No role check needed, accessible by authenticated users
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user)
        throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
      const token = await user.getIdToken(true);
      const response = await apiFetchLlmConfigurations(token);
      if (response.status) {
        setLlmConfigs(response.data);
        return response.data;
      } else {
        setError(response.message || 'Failed LLM config fetch');
        setLlmConfigs({});
        return response;
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchLlmConfigurations'); // <-- Call our complete utility
      setError(formattedError);
      setLlmConfigs({});
      throw formattedError;
    }
  }, [setError, setLlmConfigs]);

  const fetchUserApiKeyStatus = useCallback(
    async (providerName) => {
      console.debug(
        `[DataContext.fetchUserApiKeyStatus] Fetching status for provider: ${providerName}`,
      );
      // No role check needed, accessible by authenticated users
      // No modal loading/error state needed here, handle locally in modal
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiFetchUserApiKeyStatus(token, providerName); // Call API layer

        // API layer handles errors and returns standard format
        if (response.status) {
          console.debug(
            `[DataContext.fetchUserApiKeyStatus] Success for ${providerName}. Exists: ${response.data.apiKeyExists}`,
          );
          return { status: true, data: response.data }; // { apiKeyExists: boolean }
        } else {
          // API layer returned an error structure
          console.error(
            `[DataContext.fetchUserApiKeyStatus] API Error for ${providerName}:`,
            response.message,
          );
          // Return the error object for the modal to handle
          return response;
        }
      } catch (err) {
        // Catch errors from auth, network, etc.
        const formattedError = formatFirebaseError(
          err,
          'fetchUserApiKeyStatus',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
      // No loading/error dispatch needed here, handled in component
    },
    [setError],
  ); // Dependency: currentUser

  const upsertUserApiKey = useCallback(
    async (providerName, apiKey) => {
      console.debug(
        `[DataContext.upsertUserApiKey] Upserting for provider: ${providerName}`,
      );
      // No role check needed
      // Loading/Error state will be handled in the modal component
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiUpsertUserApiKey(token, providerName, apiKey); // Call API layer

        // API layer returns standard format
        if (response.status) {
          console.debug(
            `[DataContext.upsertUserApiKey] Success for ${providerName}.`,
          );
          return { status: true, message: response.message };
        } else {
          console.error(
            `[DataContext.upsertUserApiKey] API Error for ${providerName}:`,
            response.message,
          );
          return response; // Return error object
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'upsertUserApiKey'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  ); // Dependency: currentUser

  // --- NEW API Wrapper for Deleting User API Key ---
  const deleteUserApiKey = useCallback(
    async (providerName) => {
      console.debug(
        `[DataContext.deleteUserApiKey] Deleting key for provider: ${providerName}`,
      );
      // No context loading/error state for this, handle locally
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiDeleteUserApiKey(token, providerName); // Call API layer

        if (response.status) {
          console.debug(
            `[DataContext.deleteUserApiKey] Success for ${providerName}.`,
          );
          return { status: true, message: response.message };
        } else {
          // API layer returned an error structure (e.g., 404 Not Found)
          console.error(
            `[DataContext.deleteUserApiKey] API Error for ${providerName}:`,
            response.message,
          );
          return response; // Return the error object
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'deleteUserApiKey'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  ); // Dependency: currentUser

  const fetchTaskTemplates = useCallback(async () => {
    clearError(); // Clear global error
    console.debug(
      '[DataContext.fetchTaskTemplates] Fetching task templates...',
    );

    // No Admin check needed here, API endpoint allows all authenticated users

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        setTaskTemplates([]);
        return null;
      }
      const token = await user.getIdToken(true);
      const response = await apiFetchTaskTemplates(token); // Call API layer

      if (response.status) {
        console.log(
          '[fetchTaskTemplates] DATA CONTEXT: Data before calling setTaskTemplates',
          response.data.map((t) => t.taskType),
        );
        setTaskTemplates(response.data); // Update state on success
        console.debug(
          '[DataContext.fetchTaskTemplates] Success:',
          response.data.length,
          'templates fetched.',
        );
        return response.data;
      } else {
        console.error(
          '[DataContext.fetchTaskTemplates] API Error:',
          response.message,
        );
        setError(
          response.message || ERROR_MESSAGES[ERROR_CODES.INTERNAL_SERVER_ERROR],
        );
        setTaskTemplates([]);
        return response; // Return error object
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchTaskTemplates'); // <-- Call our complete utility
      setError(formattedError);
      setTaskTemplates([]);
      throw formattedError;
    }
  }, [setError, clearError, setTaskTemplates]); // Added dependencies

  // --- CORRECTED User Task Template Override Update ---
  const updateTaskTemplate = useCallback(
    async (taskType, updateData) => {
      setLlmConfigModalLoading(true); // Use LLM modal specific loading state
      setLlmConfigModalError(null); // Clear LLM modal specific error state
      // clearError(); // Optional: Clear global error? Usually not needed for modal-specific actions.

      console.debug(
        `[DataContext.updateTaskTemplate] Updating user override for ${taskType}`,
      );

      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          // Throw error to be caught by the catch block below
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        // NO userProfile role check needed here - API allows authenticated users

        const token = await user.getIdToken(true);

        // updateData should only contain { prompt?, followUpText?, followUpPrompt? }
        const response = await apiUpdateTaskTemplate(
          token,
          taskType,
          updateData,
        ); // Call API layer

        if (response.status) {
          // Update the template in the local state list using JS keys
          const updatedStateData = { taskType: taskType, ...updateData };
          updateTaskTemplateInList(updatedStateData); // Update context state
          console.debug(
            '[DataContext.updateTaskTemplate] Success:',
            response.message,
          );
          return { status: true, message: response.message }; // Indicate success to caller (modal)
        } else {
          // API layer returned an error structure { status: false, message: ..., error: ... }
          console.error(
            '[DataContext.updateTaskTemplate] API Error:',
            response.message,
          );
          setLlmConfigModalError(
            response.message || 'Failed to save configuration.',
          ); // Set modal error
          return response; // Return the error object from API layer to the modal
        }
      } catch (err) {
        // Catches errors from auth, network, or unexpected issues
        const formattedError = formatFirebaseError(err, 'updateTaskTemplate'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setLlmConfigModalLoading(false); // Turn off LLM modal specific loading state
      }
      // --- Removed userProfile from dependency array ---
      // --- Removed setError if only using modal error ---
    },
    [
      setLlmConfigModalLoading,
      setLlmConfigModalError,
      updateTaskTemplateInList,
      setError,
    ],
  ); // Dependencies updated

  // =======================================================================
  // === TODO PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  // 1) createTask wrapper
  const createTask = useCallback(
    async (params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        return await apiCreateTask(token, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'createTask'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  // 2) updateTask wrapper
  const updateTask = useCallback(
    async (todoId, params) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        return await apiUpdateTask(token, todoId, params);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'updateTask'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  // 3) deleteTask wrapper
  const deleteTask = useCallback(
    async (todoId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        return await apiDeleteTask(token, todoId);
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'deleteTask'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  // 4) fetchPendingEmailTask wrapper
  const fetchPendingEmailTask = useCallback(
    async (influencerId) => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
          return;
        }
        const token = await user.getIdToken(true);
        return await apiFetchPendingEmailTask(token, influencerId);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchPendingEmailTask',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError],
  );

  const fetchTasks = useCallback(
    async (filters = {}) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const token = await user.getIdToken(true);
        const result = await apiFetchTasks(token, filters); // Call the API wrapper

        // --- Robust Check ---
        if (result && !result.error && Array.isArray(result)) {
          // SUCCESS: It's not an error object and it IS an array
          setTasks(result);
          console.log('Tasks Ordered list:', result);
        } else if (result && result.error) {
          // API returned a structured error
          console.error('API Error fetching tasks:', result.message);
          setError(result.message);
          setTasks([]); // Set to empty array on known API error
        } else {
          // UNEXPECTED SUCCESS RESPONSE: API returned 200 but not an array
          console.warn(
            '[fetchTasks] Received unexpected non-array data on success:',
            result,
          );
          setTasks([]); // Set to empty array on unexpected data format
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'fetchTasks'); // <-- Call our complete utility
        setError(formattedError);
        setTasks([]); // Set to empty array on unexpected wrapper error
        return formattedError;
      } finally {
        setLoading(false);
      }
    },
    [setError, setTasks, setLoading],
  ); // Dependencies remain the same

  // --- NEW: Reschedule tasks wrapper ---
  const rescheduleTasks = useCallback(
    async (todoIds, newDate) => {
      if (!currentUser) {
        const msg = 'User not authenticated.';
        setError(msg);
        return { status: false, message: msg };
      }
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiRescheduleTasks(token, todoIds, newDate);
        if (result && !result.error) {
          // On success, update the local state
          updateTasksDate(new Set(todoIds), newDate);
          return { status: true, data: result };
        } else {
          setError(result.message || 'Failed to reschedule tasks.');
          return { status: false, message: result.message };
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'rescheduleTasks'); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError, updateTasksDate],
  );

  const fetchBatchInfluencerData = useCallback(
    async (ids) => {
      if (!currentUser) return { status: false, message: 'Not authenticated' };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiFetchBatchInfluencerData(token, ids);
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'fetchBatchInfluencerData',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [currentUser, setError],
  );

  // =======================================================================
  // === AI CHAT PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  // Fetch initial list of chats
  const fetchUserChats = useCallback(async () => {
    if (!currentUser) return; // Need user to be logged in
    setLoading(true);
    setError(null);
    console.log('[DataContext] Fetching user chats...');
    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiFetchChats(token);
      if (result && !result.error) {
        console.log('[DataContext] Fetched chats:', result);
        dispatch({ type: actionTypes.SET_AI_CHATS, payload: result });
      } else {
        console.error('[DataContext] Error fetching chats:', result);
        setError(result?.message || 'Failed to fetch chats.');
        dispatch({ type: actionTypes.SET_AI_CHATS, payload: [] }); // Clear on error
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchUserChats'); // <-- Call our complete utility
      setError(formattedError);
      dispatch({ type: actionTypes.SET_AI_CHATS, payload: [] }); // Clear on error
      return formattedError;
    } finally {
      setLoading(false);
    }
  }, [currentUser, setLoading, setError]);

  // =======================================================================
  // === USER PROFILE SETTINGS PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchUserSettings = useCallback(async () => {
    console.debug('[DataContext.fetchUserSettings] Fetching...');
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user)
        throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
      const token = await user.getIdToken(true);
      const response = await apiFetchUserSettings(token);
      if (response.status) {
        setUserSettings(response.data);
        return response.data;
      } else {
        setError(response.message || 'Failed user settings fetch');
        setUserSettings(null);
        return response;
      }
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'analyzeOnHoldInfluencer',
      ); // <-- Call our complete utility
      setError(formattedError);
      setUserSettings(null);
      throw formattedError;
    }
  }, [setError, setUserSettings]);

  const updateUserSettings = useCallback(
    async (settingsUpdate) => {
      console.debug('[DataContext.updateUserSettings] Updating...');
      // Add loading state if needed for UI feedback during save
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiUpdateUserSettings(token, settingsUpdate);
        if (response.status) {
          // Re-fetch or update local state
          const updatedSettings = await fetchUserSettings(); // Re-fetch to confirm
          return { status: true, data: updatedSettings?.data };
        } else {
          setError(response.message || 'Failed user settings update');
          return response;
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      }
    },
    [setError, fetchUserSettings],
  ); // Depends on fetchUserSettings to update state

  // =======================================================================
  // === ADMINISTRATOR ONLY COMPANY SETTINGS PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  // --- NEW API Wrappers for Company Management ---
  const fetchCompanyData = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setCompanyModalError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
      return null;
    }
    // Optional: Check role here too, although backend enforces it
    if (userProfile?.role !== 'Admin') {
      setCompanyModalError('Access Denied: Admins only.');
      return null;
    }

    setIsCompanyModalLoading(true);
    setCompanyModalError(null); // Clear previous errors
    let result = null;
    try {
      const token = await user.getIdToken(true);
      result = await apiFetchCompanyData(token);

      if (result?.status) {
        setCompanyData(result.data || {}); // Set data (or empty obj if null/undefined)
      } else {
        // API returned status: false or had an error structure
        setCompanyModalError(
          result?.message || 'Failed to fetch company data.',
        );
        setCompanyData(null); // Clear data on error
      }
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'analyzeOnHoldInfluencer',
      ); // <-- Call our complete utility
      setError(formattedError);
      setCompanyData(null); // Clear data on unexpected error
      return formattedError;
    } finally {
      setIsCompanyModalLoading(false);
    }
    return result; // Return the result object
  }, [
    userProfile,
    setIsCompanyModalLoading,
    setCompanyModalError,
    setCompanyData,
    setError,
  ]); // Dependencies

  const updateCompanyData = useCallback(
    async (companyDetails) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setCompanyModalError(
          ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED],
        );
        return {
          status: false,
          error: ERROR_CODES.USER_NOT_AUTHENTICATED,
          message: ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED],
        };
      }
      // Optional: Check role here too
      if (userProfile?.role !== 'Admin') {
        setCompanyModalError('Access Denied: Admins only.');
        return {
          status: false,
          error: ERROR_CODES.FORBIDDEN,
          message: 'Access Denied: Admins only.',
        };
      }

      setIsCompanyModalLoading(true);
      setCompanyModalError(null);
      let result = null;
      try {
        const token = await user.getIdToken(true);
        result = await apiUpdateCompanyData(token, companyDetails);

        if (result?.status) {
          console.log(
            '[DataContext updateCompanyData] Update successful, re-fetching data...',
          );
          await fetchCompanyData(); // Re-fetch data to ensure UI consistency
        } else {
          // API returned status: false or had an error structure
          setCompanyModalError(
            result?.message || 'Failed to update company data.',
          );
          // Don't clear local data on update failure, let user retry/correct
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setIsCompanyModalLoading(false);
      }
      return result; // Return the result object
    },
    [
      userProfile,
      setIsCompanyModalLoading,
      setCompanyModalError,
      fetchCompanyData,
      setError,
    ],
  ); // fetchCompanyData is a dependency!

  // =======================================================================
  // === ADMINISTRATOR ONLY PRODUCT SETTINGS PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchProducts = useCallback(async () => {
    clearError(); // Clear global error first
    setProductModalError(null); // Clear modal-specific error
    // Don't set loading here, might be called in background
    console.debug('[DataContext.fetchProducts] Fetching product list...');
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setError(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        setProducts([]); // Clear products on auth error
        return null;
      }
      const token = await user.getIdToken(true);
      const response = await apiFetchProducts(token); // Call the API layer

      if (response.status) {
        setProducts(response.data); // Update state on success
        console.debug(
          '[DataContext.fetchProducts] Success:',
          response.data.length,
          'products fetched.',
        );
        return response.data; // Return data for potential chaining
      } else {
        // API layer returned an error structure
        console.error(
          '[DataContext.fetchProducts] API Error:',
          response.message,
        );
        setError(
          response.message || ERROR_MESSAGES[ERROR_CODES.INTERNAL_SERVER_ERROR],
        ); // Set global error
        setProducts([]); // Clear products on API error
        return response; // Return the error object
      }
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'analyzeOnHoldInfluencer',
      ); // <-- Call our complete utility
      setError(formattedError);
      setProducts([]); // Clear products on unexpected error
      throw formattedError;
    }
  }, [setError, clearError, setProducts, setProductModalError]); // Add dependencies

  const createProduct = useCallback(
    async (productName, description, goToMarket) => {
      setProductModalLoading(true);
      setProductModalError(null); // Clear previous modal error
      clearError(); // Clear global error
      console.debug(
        `[DataContext.createProduct] Creating product: ${productName}`,
      );
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await user.getIdToken(true);
        const response = await apiCreateProduct(
          token,
          productName,
          description,
          goToMarket,
        );

        if (response.status) {
          addProductToList(response.data); // Add the new product to the list
          console.debug('[DataContext.createProduct] Success:', response.data);
          return { status: true, data: response.data }; // Indicate success
        } else {
          console.error(
            '[DataContext.createProduct] API Error:',
            response.message,
          );
          setProductModalError(response.message || 'Failed to create product.'); // Set modal error
          return response; // Return the error object
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setProductModalLoading(false);
      }
    },
    [
      clearError,
      addProductToList,
      setProductModalLoading,
      setProductModalError,
      setError,
    ],
  ); // Add dependencies

  const updateProduct = useCallback(
    async (productId, productName, description, goToMarket) => {
      setProductModalLoading(true);
      setProductModalError(null);
      clearError();
      console.debug(
        `[DataContext.updateProduct] Updating product ID: ${productId}`,
      );
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await user.getIdToken(true);
        const response = await apiUpdateProduct(
          token,
          productId,
          productName,
          description,
          goToMarket,
        );

        if (response.status) {
          // Need the full product object to update the list correctly
          const updatedProductData = {
            productsListId: productId,
            productName,
            description,
            goToMarket,
          };
          updateProductInList(updatedProductData); // Update the list
          console.debug(
            '[DataContext.updateProduct] Success:',
            response.message,
          );
          return { status: true, message: response.message }; // Indicate success
        } else {
          console.error(
            '[DataContext.updateProduct] API Error:',
            response.message,
          );
          setProductModalError(response.message || 'Failed to update product.');
          return response;
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setProductModalLoading(false);
      }
    },
    [
      clearError,
      updateProductInList,
      setProductModalLoading,
      setProductModalError,
      setError,
    ],
  ); // Add dependencies

  const deleteProduct = useCallback(
    async (productId) => {
      setProductModalLoading(true);
      setProductModalError(null);
      clearError();
      console.debug(
        `[DataContext.deleteProduct] Deleting product ID: ${productId}`,
      );

      // Store the ID being deleted before making the call
      const originalSelectedId = state.selectedProductId;

      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        const token = await user.getIdToken(true);
        const response = await apiDeleteProduct(token, productId);

        if (response.status) {
          removeProductFromList(productId); // Remove from the list
          console.debug(
            '[DataContext.deleteProduct] Success:',
            response.message,
          );
          // If the deleted product was the one selected, reset selection to 'new'
          if (originalSelectedId === productId) {
            setSelectedProductId('new');
          }
          return { status: true, message: response.message }; // Indicate success
        } else {
          console.error(
            '[DataContext.deleteProduct] API Error:',
            response.message,
          );
          setProductModalError(response.message || 'Failed to delete product.');
          return response;
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setProductModalLoading(false);
      }
      // Added state.selectedProductId to dependency array
    },
    [
      clearError,
      removeProductFromList,
      setSelectedProductId,
      setProductModalLoading,
      setProductModalError,
      setError,
      state.selectedProductId,
    ],
  );

  // =======================================================================
  // === ADMINISTRATOR ONLY LLM CONFIG PAGE API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  const fetchAdminLlmConfigurations = useCallback(async () => {
    setLlmAdminModalLoading(true);
    setLlmAdminModalError(null);
    console.debug('[DataContext.fetchAdminLlmConfigurations] Fetching...');
    try {
      // Role check before API call
      if (userProfile?.role !== 'Admin') {
        // Check userProfile from context scope
        // Use ERROR_CODES and ERROR_MESSAGES defined/imported in this file
        throw new Error(
          ERROR_MESSAGES[ERROR_CODES.FORBIDDEN] || 'Admin privileges required.',
        );
      }
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user)
        throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
      const token = await user.getIdToken(true);
      const response = await apiFetchAdminLlmConfigurations(token); // Call API layer

      if (response.status) {
        setAdminLlmConfigs(response.data);
        return { status: true, data: response.data };
      } else {
        // Error handled by API layer, set modal error state
        setLlmAdminModalError(
          response.message || 'Failed admin LLM config fetch',
        );
        setAdminLlmConfigs([]);
        return response; // Return the error structure
      }
    } catch (err) {
      // Catch errors from role check, auth, or unexpected issues
      const formattedError = formatFirebaseError(
        err,
        'analyzeOnHoldInfluencer',
      ); // <-- Call our complete utility
      setError(formattedError);
      setAdminLlmConfigs([]);
      throw formattedError;
    } finally {
      setLlmAdminModalLoading(false);
    }
    // --- Add userProfile to dependency array ---
  }, [
    userProfile,
    setAdminLlmConfigs,
    setLlmAdminModalLoading,
    setLlmAdminModalError,
    setError,
  ]);

  const addLlmConfiguration = useCallback(
    async (providerName, modelName, idioms, isAvailable) => {
      setLlmAdminModalLoading(true);
      setLlmAdminModalError(null);
      console.debug('[DataContext.addLlmConfiguration] Adding...');
      try {
        if (userProfile?.role !== 'Admin') {
          // Check userProfile
          throw new Error(
            ERROR_MESSAGES[ERROR_CODES.FORBIDDEN] ||
              'Admin privileges required.',
          );
        }
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiAddLlmConfiguration(
          token,
          providerName,
          modelName,
          idioms,
          isAvailable,
        ); // Call API layer

        if (response.status) {
          // Checks for 201 or 207
          if (response.added && response.added.length > 0) {
            addAdminLlmConfig(response.added); // Add to local state
          }
          // Re-fetch the grouped configs for the user modal
          fetchLlmConfigurations(); // Trigger fetch for user view
          return response; // Return full response including potential errors/partial success
        } else {
          setLlmAdminModalError(response.message || 'Failed to add LLM config');
          return response; // Return error structure from API layer
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setLlmAdminModalLoading(false);
      }
      // --- Add userProfile and fetchLlmConfigurations to dependency array ---
    },
    [
      userProfile,
      addAdminLlmConfig,
      fetchLlmConfigurations,
      setLlmAdminModalLoading,
      setLlmAdminModalError,
      setError,
    ],
  );

  const updateLlmConfiguration = useCallback(
    async (llmConfigId, updateData) => {
      setLlmAdminModalLoading(true);
      setLlmAdminModalError(null);
      console.debug(
        `[DataContext.updateLlmConfiguration] Updating ID ${llmConfigId}...`,
      );
      try {
        if (userProfile?.role !== 'Admin') {
          // Check userProfile
          throw new Error(
            ERROR_MESSAGES[ERROR_CODES.FORBIDDEN] ||
              'Admin privileges required.',
          );
        }
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiUpdateLlmConfiguration(
          token,
          llmConfigId,
          updateData,
        ); // Call API layer

        if (response.status) {
          if (response.updated) {
            updateAdminLlmConfig(response.updated); // Update local admin list state
          }
          // Re-fetch the grouped configs for the user modal
          fetchLlmConfigurations(); // Trigger fetch for user view
          return response;
        } else {
          setLlmAdminModalError(
            response.message || 'Failed to update LLM config',
          );
          return response; // Return error structure from API layer
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setLlmAdminModalLoading(false);
      }
      // --- Add userProfile, updateAdminLlmConfig, fetchLlmConfigurations to dependency array ---
    },
    [
      userProfile,
      updateAdminLlmConfig,
      fetchLlmConfigurations,
      setLlmAdminModalLoading,
      setLlmAdminModalError,
      setError,
    ],
  );

  const deleteLlmConfiguration = useCallback(
    async (llmConfigId) => {
      setLlmAdminModalLoading(true);
      setLlmAdminModalError(null);
      console.debug(
        `[DataContext.deleteLlmConfiguration] Deleting ID ${llmConfigId}...`,
      );
      try {
        if (userProfile?.role !== 'Admin') {
          // Check userProfile
          throw new Error(
            ERROR_MESSAGES[ERROR_CODES.FORBIDDEN] ||
              'Admin privileges required.',
          );
        }
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user)
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        const token = await user.getIdToken(true);
        const response = await apiDeleteLlmConfiguration(token, llmConfigId); // Call API layer

        if (response.status) {
          deleteAdminLlmConfig(llmConfigId); // Update local admin list state
          // Re-fetch the grouped configs for the user modal
          fetchLlmConfigurations(); // Trigger fetch for user view
          return response;
        } else {
          setLlmAdminModalError(
            response.message || 'Failed to delete LLM config',
          );
          return response; // Return error structure from API layer
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setLlmAdminModalLoading(false);
      }
      // --- Add userProfile, deleteAdminLlmConfig, fetchLlmConfigurations to dependency array ---
    },
    [
      userProfile,
      deleteAdminLlmConfig,
      fetchLlmConfigurations,
      setLlmAdminModalLoading,
      setLlmAdminModalError,
      setError,
    ],
  );

  // =======================================================================
  // === ADMINISTRATION ONLY USER SETTINGS API WRAPPERS (Corrected & Production Ready) ===
  // =======================================================================

  // --- NEW API Wrappers for Admin Tasks Modal ---

  const fetchUsers = useCallback(async () => {
    setAdminTasksLoading(true);
    setAdminTasksError(null);
    console.debug('[DataContext.fetchUsers] Fetching all users...');
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
      }
      // Admin check is done on the backend endpoint
      const token = await user.getIdToken(true);
      const response = await apiFetchUsers(token); // Call the new API layer function

      if (response.status) {
        setAllUsers(response.data); // Update state with the list of users
        console.debug(
          '[DataContext.fetchUsers] Success:',
          response.data.length,
          'users fetched.',
        );
        return { status: true, data: response.data };
      } else {
        console.error('[DataContext.fetchUsers] API Error:', response.message);
        const errMsg = response.message || ERROR_CODES.INTERNAL_SERVER_ERROR;
        setAdminTasksError(errMsg); // Set modal-specific error
        setAllUsers([]); // Clear list on error
        return response; // Return the error object
      }
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'analyzeOnHoldInfluencer',
      ); // <-- Call our complete utility
      setError(formattedError);
      setAllUsers([]); // Clear list on error
      return formattedError;
    } finally {
      setAdminTasksLoading(false);
    }
  }, [setAdminTasksLoading, setAdminTasksError, setAllUsers, setError]); // Dependencies: setters and currentUser

  const signupUserAdmin = useCallback(
    async (userData) => {
      // userData expected: { userName, email, password }
      setAdminTasksLoading(true);
      setAdminTasksError(null);
      console.debug(
        '[DataContext.signupUserAdmin] Attempting to signup new user:',
        userData.email,
      );
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        // Admin check is done on the backend endpoint
        const token = await user.getIdToken(true);
        const response = await apiAdminSignupUser(token, userData); // Call new API layer function

        if (response.status) {
          // Check for 201 or 200 range success status from api wrapper
          console.debug(
            '[DataContext.signupUserAdmin] Success:',
            response.message,
            response.data,
          );
          // Add the new user to the local list (backend returns minimal user info)
          // The backend response should include userId, userName, email, role.
          // Let's assume response.data has { userId, userName, email }. Role is 'Manager'.
          addUserToAllUsersList({
            userId: response.data.userId,
            userName: userData.userName, // Use the provided userName for immediate state update
            email: userData.email,
            role: 'Manager', // Default role from backend
          });
          return {
            status: true,
            message: response.message,
            data: response.data,
          };
        } else {
          console.error(
            '[DataContext.signupUserAdmin] API Error:',
            response.message,
            response.error,
          );
          const errMsg =
            response.message ||
            ERROR_MESSAGES[response.error] ||
            ERROR_CODES.INTERNAL_SERVER_ERROR;
          setAdminTasksError(errMsg); // Set modal-specific error
          return response; // Return the error object from API layer
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setAdminTasksLoading(false);
      }
    },
    [setAdminTasksLoading, setAdminTasksError, addUserToAllUsersList, setError],
  ); // Dependencies

  const moveInfluencers = useCallback(
    async (fromUserId, toUserId, influencerIdsToMove) => {
      // influencerIdsToMove can be array or 'all'
      setAdminTasksLoading(true);
      setAdminTasksError(null);
      console.debug(
        `[DataContext.moveInfluencers] Moving from ${fromUserId} to ${toUserId}, Influencers:`,
        influencerIdsToMove,
      );
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        // Admin check is done on the backend endpoint
        const token = await user.getIdToken(true);
        const response = await apiMoveInfluencers(
          token,
          fromUserId,
          toUserId,
          influencerIdsToMove,
        ); // Call new API layer function

        if (response.status) {
          console.debug(
            '[DataContext.moveInfluencers] Success:',
            response.message,
            response.data,
          );
          // Note: Moving influencers doesn't require updating local influencer state immediately.
          // The user fetching influencer data will get the updated userId next time.
          // The main state affected is the backend DB.
          return {
            status: true,
            message: response.message,
            data: response.data,
          };
        } else {
          console.error(
            '[DataContext.moveInfluencers] API Error:',
            response.message,
            response.error,
          );
          const errMsg =
            response.message ||
            ERROR_MESSAGES[response.error] ||
            ERROR_CODES.INTERNAL_SERVER_ERROR;
          setAdminTasksError(errMsg); // Set modal-specific error
          return response; // Return the error object from API layer
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setAdminTasksLoading(false);
      }
    },
    [setAdminTasksLoading, setAdminTasksError, setError],
  ); // Dependencies

  const deleteUserAdmin = useCallback(
    async (userIdToDelete) => {
      setAdminTasksLoading(true);
      setAdminTasksError(null);
      console.debug(
        `[DataContext.deleteUserAdmin] Attempting to delete user: ${userIdToDelete}`,
      );
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error(ERROR_MESSAGES[ERROR_CODES.USER_NOT_AUTHENTICATED]);
        }
        // Admin check is done on the backend endpoint
        const token = await user.getIdToken(true);

        // Pass the userId to the admin delete endpoint
        const response = await apiDeleteUserAdmin(token, userIdToDelete); // Call new API layer function

        if (response.status) {
          // Check for 200 status range success
          console.debug(
            '[DataContext.deleteUserAdmin] Success:',
            response.message,
          );
          // Remove the user from the local list
          removeUserFromAllUsersList(userIdToDelete);
          // If the deleted user was the active influencer's owner, clear queryResults?
          // This is complex. Let's assume UI handles refreshing or redirecting if active user is deleted.
          // Or perhaps clear relevant state if the DELETED user is the current user profile?
          // If currentUser.uid == userIdToDelete, this shouldn't happen due to backend check,
          // but if it somehow did, the app would likely break and need reload anyway.
          return { status: true, message: response.message };
        } else {
          console.error(
            '[DataContext.deleteUserAdmin] API Error:',
            response.message,
            response.error,
          );
          const errMsg = response.message || ERROR_CODES.INTERNAL_SERVER_ERROR;
          // Check for the specific error code if user has influencers
          if (response.error === ERROR_CODES.NOT_INFLUENCER_OWNER) {
            setAdminTasksError(errMsg); // Use the backend message
          } else {
            setAdminTasksError(errMsg); // Use generic error message
          }
          return response; // Return the error object from API layer
        }
      } catch (err) {
        const formattedError = formatFirebaseError(
          err,
          'analyzeOnHoldInfluencer',
        ); // <-- Call our complete utility
        setError(formattedError);
        return formattedError;
      } finally {
        setAdminTasksLoading(false);
      }
    },
    [
      setAdminTasksLoading,
      setAdminTasksError,
      removeUserFromAllUsersList,
      setError,
    ],
  ); // Dependencies

  // 6. Create the API Wrappers (add these with your other wrappers):
  const fetchAllSystemUsers = useCallback(async () => {
    setSuperAdminLoading(true);
    setSuperAdminError(null);
    try {
      if (!currentUser) throw new Error('Not authenticated.');
      const token = await currentUser.getIdToken(true);
      const response = await apiFetchAllSystemUsers(token);

      if (response.status) {
        setSystemUsers(response.data);
        return { status: true, data: response.data };
      } else {
        setSuperAdminError(response.message || 'Failed to fetch users.');
        setSystemUsers([]);
        return response;
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchAllSystemUsers');
      setError(formattedError);
      return formattedError;
    } finally {
      setSuperAdminLoading(false);
    }
  }, [
    currentUser,
    setSuperAdminLoading,
    setSuperAdminError,
    setSystemUsers,
    setError,
  ]);

  const onboardNewCompany = useCallback(
    async (companyData) => {
      setSuperAdminLoading(true);
      setSuperAdminError(null);
      try {
        if (!currentUser) throw new Error('Not authenticated.');
        const token = await currentUser.getIdToken(true);
        const response = await apiOnboardCompany(token, companyData);

        if (response.status) {
          return {
            status: true,
            message: response.message,
            data: response.data,
          };
        } else {
          setSuperAdminError(response.message || 'Failed to onboard company.');
          return response;
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'onboardNewCompany');
        setError(formattedError);
        return formattedError;
      } finally {
        setSuperAdminLoading(false);
      }
    },
    [currentUser, setSuperAdminLoading, setSuperAdminError, setError],
  );

  const superAdminScrapeWebsite = useCallback(
    async (targetCompanyId, url) => {
      // We don't set global superAdminLoading here because we want to control
      // the specific UI messages inside the modal during this long process.
      try {
        if (!currentUser) throw new Error('Not authenticated.');
        const token = await currentUser.getIdToken(true);
        const response = await apiSuperAdminScrapeWebsite(
          token,
          targetCompanyId,
          url,
        );
        return response;
      } catch (err) {
        return { status: false, message: err.message };
      }
    },
    [currentUser],
  );

  const executeImpersonation = useCallback(
    async (targetUid) => {
      setSuperAdminLoading(true);
      setSuperAdminError(null);
      try {
        if (!currentUser) throw new Error('Not authenticated.');
        const token = await currentUser.getIdToken(true);

        // 1. Fetch the Magic JWT from the backend
        const response = await apiImpersonateUser(token, targetUid);

        if (response.status && response.data.token) {
          // 2. Swap identities inside Firebase Auth
          await loginWithCustomToken(response.data.token);

          // 3. Purge memory & state by forcing a hard reload.
          // The user will boot back up as the new identity immediately.
          localStorage.removeItem('appState');
          window.location.reload();
        } else {
          setSuperAdminError(response.message || 'Failed to impersonate.');
          return response;
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'executeImpersonation');
        setSuperAdminError(
          formattedError.message || 'Failed to execute identity swap.',
        );
        return formattedError;
      } finally {
        setSuperAdminLoading(false);
      }
    },
    [
      currentUser,
      loginWithCustomToken,
      setSuperAdminLoading,
      setSuperAdminError,
    ],
  );

  const fetchSystemInvoices = useCallback(async () => {
    setSuperAdminLoading(true);
    setSuperAdminError(null);
    try {
      if (!currentUser) throw new Error('Not authenticated.');
      const token = await currentUser.getIdToken(true);
      const response = await apiFetchSystemInvoices(token);

      if (response.status) {
        dispatch({
          type: actionTypes.SET_SYSTEM_INVOICES,
          payload: response.data,
        });
        return { status: true, data: response.data };
      } else {
        setSuperAdminError(response.message || 'Failed to fetch invoices.');
        return response;
      }
    } catch (err) {
      const formattedError = formatFirebaseError(err, 'fetchSystemInvoices');
      setError(formattedError);
      return formattedError;
    } finally {
      setSuperAdminLoading(false);
    }
  }, [currentUser, setSuperAdminLoading, setSuperAdminError, setError]);

  const markInvoiceAsPaid = useCallback(
    async (invoiceId) => {
      // Note: We don't set global loading here to keep the UI snappy for the specific row
      // The specific button logic will handle its own loading state if needed
      try {
        if (!currentUser) throw new Error('Not authenticated.');
        const token = await currentUser.getIdToken(true);
        const response = await apiMarkInvoicePaid(token, invoiceId);

        if (response.status) {
          // Optimistic Update: Update local state immediately
          dispatch({
            type: actionTypes.UPDATE_INVOICE_STATUS,
            payload: {
              invoiceId,
              newStatus: 'PAID',
              paidAt: new Date().toISOString(),
            },
          });
          return { status: true, message: response.message };
        } else {
          setSuperAdminError(response.message || 'Failed to update invoice.');
          return response;
        }
      } catch (err) {
        const formattedError = formatFirebaseError(err, 'markInvoiceAsPaid');
        setSuperAdminError(formattedError.message);
        return formattedError;
      }
    },
    [currentUser, setSuperAdminError],
  );

  const generateMonthlyInvoices = useCallback(async () => {
    setSuperAdminLoading(true);
    try {
      const token = await currentUser.getIdToken(true);
      const res = await apiGenerateInvoices(token);
      if (res.status) {
        toast.success(res.message || 'Statements generated.');
        fetchSystemInvoices(); // Refresh the table automatically
      } else {
        toast.error(res.message || 'Failed to generate statements.');
      }
      return res;
    } finally {
      setSuperAdminLoading(false);
    }
  }, [currentUser, fetchSystemInvoices, setSuperAdminLoading]);

  const fetchCompanyWalletTransactions = useCallback(async () => {
    if (!currentUser) return;

    setAnalyticsDashboardLoading(true);
    setAnalyticsDashboardError(null);

    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiFetchCompanyWalletTransactions(token);

      if (result.status) {
        dispatch({
          type: actionTypes.SET_COMPANY_WALLET_TRANSACTIONS,
          payload: result.data,
        });
      } else {
        setAnalyticsDashboardError(result.message);
      }
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'fetchCompanyWalletTransactions',
      );
      setAnalyticsDashboardError(formattedError.message);
    } finally {
      setAnalyticsDashboardLoading(false);
    }
  }, [currentUser, setAnalyticsDashboardLoading, setAnalyticsDashboardError]);

  const fetchSystemWalletTransactions = useCallback(async () => {
    setSuperAdminLoading(true);
    setSuperAdminError(null);
    try {
      if (!currentUser) throw new Error('Not authenticated.');
      const token = await currentUser.getIdToken(true);
      const response = await apiFetchSystemWalletTransactions(token);

      if (response.status) {
        dispatch({
          type: actionTypes.SET_SYSTEM_WALLET_TRANSACTIONS,
          payload: response.data,
        });
        return { status: true, data: response.data };
      } else {
        setSuperAdminError(
          response.message || 'Failed to fetch wallet transactions.',
        );
        return response;
      }
    } catch (err) {
      const formattedError = formatFirebaseError(
        err,
        'fetchSystemWalletTransactions',
      );
      setError(formattedError);
      return formattedError;
    } finally {
      setSuperAdminLoading(false);
    }
  }, [currentUser, setSuperAdminLoading, setSuperAdminError, setError]);

  const handleStripeCheckout = useCallback(
    async (amount) => {
      if (!currentUser) return;
      dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: true }); // START SPINNER
      try {
        const token = await currentUser.getIdToken(true);
        const res = await apiCreateStripeCheckoutSession(token, amount);
        if (res.status && res.data?.url) {
          window.location.href = res.data.url; // Unloads the page, spinner will naturally disappear
        } else {
          toast.error(res.message || 'Failed to initialize checkout.');
          dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: false }); // STOP SPINNER ON ERROR
        }
      } catch (err) {
        toast.error('An error occurred connecting to Stripe.');
        dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: false });
      }
    },
    [currentUser],
  );

  const handleStripeSetup = useCallback(async () => {
    if (!currentUser) return;
    dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: true }); // START SPINNER
    try {
      const token = await currentUser.getIdToken(true);
      const res = await apiCreateStripeSetupSession(token);
      if (res.status && res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error(res.message || 'Failed to initialize card setup.');
        dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: false });
      }
    } catch (err) {
      toast.error('An error occurred connecting to Stripe.');
      dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: false });
    }
  }, [currentUser]);

  const loadAutoTopupConfig = useCallback(async () => {
    if (!currentUser) return;
    dispatch({ type: actionTypes.SET_AUTO_TOPUP_LOADING, payload: true });
    try {
      const token = await currentUser.getIdToken(true);
      const res = await apiFetchAutoTopupConfig(token);
      if (res.status) {
        dispatch({
          type: actionTypes.SET_AUTO_TOPUP_CONFIG,
          payload: res.data,
        });
      }
    } finally {
      dispatch({ type: actionTypes.SET_AUTO_TOPUP_LOADING, payload: false });
    }
  }, [currentUser]);

  const saveAutoTopupConfig = useCallback(
    async (configData) => {
      if (!currentUser) return;
      dispatch({ type: actionTypes.SET_AUTO_TOPUP_LOADING, payload: true });
      try {
        const token = await currentUser.getIdToken(true);
        const res = await apiUpdateAutoTopupConfig(token, configData);
        if (res.status) {
          toast.success('Auto-Pilot configuration saved!');
          await loadAutoTopupConfig(); // Refresh data
        } else {
          toast.error(res.message || 'Failed to save configuration.');
        }
      } finally {
        dispatch({ type: actionTypes.SET_AUTO_TOPUP_LOADING, payload: false });
      }
    },
    [currentUser, loadAutoTopupConfig],
  );

  // tries a network reconnection when triggered by the user
  const handleRetryConnection = useCallback(async () => {
    if (isRetrying) {
      console.warn(
        '[DataContext] Retry ignored; an attempt is already in progress.',
      );
      return;
    }

    try {
      dispatch({ type: actionTypes.SET_IS_RETRYING, payload: true });
      const result = await apiHealthCheck();

      if (result.status) {
        console.log(
          '[DataContext] RETRY LOGIC: Health check SUCCEEDED. Fixing localStorage state and reloading.',
        );

        // 1. Manually fix the state in localStorage BEFORE reloading to solve the race condition.
        const LOCAL_STORAGE_KEY = 'appState';
        const storedState = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedState) {
          const parsedState = JSON.parse(storedState);
          const correctedState = {
            ...parsedState,
            isOffline: false,
            isRetrying: false,
            error: null,
          };
          localStorage.setItem(
            LOCAL_STORAGE_KEY,
            JSON.stringify(correctedState),
          );
          console.log(
            "[DataContext] Manually updated 'appState' in localStorage before reload.",
          );
        }

        // 2. Reload the page to reset Firebase Auth.
        window.location.reload();
      } else {
        console.error(
          `[DataContext] RETRY LOGIC: Health check FAILED. Forcing offline state atomically.`,
        );
        // The dispatch is enough. The "Sync with localStorage" useEffect will automatically save the new state.
        dispatch({ type: actionTypes.SET_RETRY_FAILED });
        throw error;
      }
    } catch (err) {
      console.error('[DataContext] Error in handleRetryConnection:', err);
      const formattedError = formatFirebaseError(err, 'handleRetryConnection');
      setError(formattedError);
      // The dispatch is enough.
      dispatch({ type: actionTypes.SET_RETRY_FAILED });
      throw formattedError;
    }
  }, [isRetrying, dispatch, error, setError]);

  // GROUP OF USEEFFECTS() WHICH HANDLES MANAGEMENT OF GLOBAL STATES

  // --- STRIPE REDIRECT INTERCEPTOR ---
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isPaymentSuccess = params.get('payment_success') === 'true';
    const isSetupSuccess = params.get('setup_success') === 'true';
    const isPaymentCanceled = params.get('payment_canceled') === 'true';

    if (isPaymentSuccess || isSetupSuccess || isPaymentCanceled) {
      // --- THE FIX: Force the spinner off immediately upon return ---
      dispatch({ type: actionTypes.SET_STRIPE_LOADING, payload: false });
      // 1. Set the specific tab target BEFORE opening the modal
      dispatch({
        type: actionTypes.SET_ANALYTICS_INITIAL_TAB,
        payload: 'topup',
      });

      // 2. Open the modal
      dispatch({
        type: actionTypes.SET_IS_ANALYTICS_MODAL_OPEN,
        payload: true,
      });

      // 3. Handle specific feedback
      if (isPaymentSuccess) {
        const amt = params.get('amount') || '';
        toast.success(`Successfully added $${amt} to your workspace wallet!`);
        if (currentUser) retrieveUserProfile(); // Refresh the balance
      } else if (isSetupSuccess) {
        toast.success('Payment method securely saved for Auto-Pilot!');
      } else if (isPaymentCanceled) {
        toast.warn('Payment was canceled.');
      }

      // 4. SCRUB THE URL (Prevents logic from re-firing on reload)
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]); // Only needs to run when the user first lands/logs in

  // This is the Network detector, setting the state of isOffline
  useEffect(() => {
    console.log(
      '[DataContext] EFFECT (Network Detector): Checking for network error. Current error:',
      error,
      'Current isOffline state:',
      isOffline,
    );
    if (
      error &&
      (error.code === 'auth/network-request-failed' ||
        error.code === ERROR_CODES.NETWORK_ERROR)
    ) {
      if (!isOffline) {
        console.error(
          '[DataContext] CRITICAL: Network error detected! Setting persistent offline state.',
        );

        // The dispatch is enough. The "Sync with localStorage" useEffect will automatically save the new state.
        dispatch({ type: actionTypes.SET_IS_OFFLINE, payload: true });
      }
    }
  }, [error, isOffline, dispatch]);

  // Fetch chats when user logs in
  useEffect(() => {
    if (currentUser && !isOffline) {
      fetchUserChats();
    }
  }, [currentUser, isOffline, fetchUserChats]);

  // --- useEffect to fetch missing influencer data" ---
  useEffect(() => {
    if (isOffline || !currentUser || !activeinfluencerId) return;

    // Check if the active influencer is missing from the current list
    const isInfluencerMissing = !queryResults?.some(
      (item) => item.influencerId === activeinfluencerId,
    );

    if (isInfluencerMissing) {
      console.debug(
        `[DataContext] Active Influencer ${activeinfluencerId} missing. Fetching...`,
      );

      const fetchMissingInfluencer = async () => {
        setIsLoadingQueryData(true);
        try {
          // 1. FIX: Use the CORRECT wrapper (from InfluencerPageApi.js)
          // This endpoint returns the full detailed object needed for the UI
          const result = await getInfluencerDetails(activeinfluencerId);

          if (result.status && result.data) {
            // 2. FIX: Handle the data structure correctly.
            // We must APPEND to the existing array, not overwrite it with a single object.
            setQueryResults((prevResults) => {
              const safePrev = Array.isArray(prevResults) ? prevResults : [];

              // If we already have it (race condition check), return as is
              if (
                safePrev.some(
                  (i) => i.influencerId === result.data.influencerId,
                )
              ) {
                return safePrev;
              }
              // Append the new influencer to the list
              return [...safePrev, result.data];
            });
          } else {
            console.error(`Fetch failed: ${result.message}`);
            setError(result.message || 'Could not load influencer details.');
            // --- THE FIX: Break the loop by clearing the invalid ID ---
            setActiveinfluencerId(null);
          }
        } catch (err) {
          console.error('Error fetching missing influencer:', err);
          // Use formatted error if available or generic string
          setError('An unexpected error occurred loading the influencer.');
          // --- THE FIX: Break the loop by clearing the invalid ID ---
          setActiveinfluencerId(null);
        } finally {
          setIsLoadingQueryData(false);
        }
      };

      fetchMissingInfluencer();
    }
  }, [
    activeinfluencerId,
    queryResults,
    currentUser,
    isOffline,
    isLoadingQueryData,
    setIsLoadingQueryData,
    getInfluencerDetails,
    setQueryResults,
    setError,
    setActiveinfluencerId,
  ]);

  // Effect to fetch user profile once authenticated user is available
  // This replaces the login page's immediate fetch. AppContent waits for authLoading.
  useEffect(() => {
    if (isOffline) {
      return;
    }
    console.log(
      '[DataContext] currentUser or location changed:',
      currentUser ? currentUser.uid : null,
      location.pathname,
    );
    // Define routes where profile fetching is NOT needed
    const publicRoutes = [
      '/login',
      '/signup',
      '/forgot-password',
      '/reset-password',
    ];

    if (currentUser && !authLoading && !isOffline) {
      // Only fetch if the user is authenticated AND
      // they are NOT on a public auth page
      if (!publicRoutes.includes(location.pathname)) {
        console.log(
          '[DataContext] User is logged in and on a protected/profile route. Fetching profile.',
        );
        // Fetch profile when user logs in or auth state changes to logged in
        retrieveUserProfile();
      }
    } else {
      // Clear profile when user logs out
      setUserProfile(null);
      // Also clear relevant modal states if they depend on user
      setIsUserProfileModalOpen(false);
      setIsLlmConfigModalOpen(false);
      setIsProductModalOpen(false); // Maybe clear admin modals too?
      setIsLlmAdminModalOpen(false);
      setIsCompanyModalOpen(false);
    }
  }, [
    currentUser,
    retrieveUserProfile,
    setUserProfile,
    location.pathname,
    setIsUserProfileModalOpen,
    setIsLlmConfigModalOpen,
    setIsProductModalOpen,
    setIsLlmAdminModalOpen,
    setIsCompanyModalOpen,
    authLoading,
    isOffline,
  ]);

  // Initial Task Fetch on User Load
  useEffect(() => {
    if (currentUser && !isOffline) {
      fetchTasks(); // Call the memoized fetch function
    } else {
      setTasks([]); // Use dispatcher
    }
  }, [currentUser, isOffline, fetchTasks, setTasks]); // Add setTasks dispatcher dependency

  const contextValue = useMemo(
    () => ({
      // Existing State (from useReducer)
      ...state,

      // Dispatcher Functions
      retrieveUserProfile,
      setIsOffline,
      productToggles,
      setProductToggles,
      setQueryResults,
      setLastQuery,
      setQueryTableSorting,
      setActiveinfluencerId,
      setActiveChannel,
      setStatus,
      setActiveInfluencerAndChannel,
      setReconnectNetwork,
      setIsUserProfileModalOpen,
      setIsUserProfileModalLoading,
      setUserProfileModalError,
      setIsCompanyModalOpen,
      setCompanyData,
      setIsCompanyModalLoading,
      setCompanyModalError,
      setProducts,
      addProductToList,
      updateProductInList,
      removeProductFromList,
      setIsProductModalOpen,
      setSelectedProductId,
      setProductModalLoading,
      setProductModalError,
      setIsMarketFitModalOpen,
      setMarketFitDashboardData,
      setIsMarketFitDashboardLoading,
      setIsContentFocusModalOpen,
      setContentFocusDashboardData,
      setIsContentFocusDashboardLoading,
      setPublications,
      setPublicationTogglingVideo,
      updatePublicationVideoData,
      setPublicationTogglingProduct,
      updatePublicationProducts,
      setPublicationGeneratingSummary,
      setPublicationAiSummary,
      generateSingleMarketFitAnalysis,
      fetchIndividualMarketFitAnalysis,
      setIndividualMarketFitAnalysis,
      clearAggregatedData,
      fetchAggregatedMarketFit,
      fetchAggregatedPublicationSummary,
      setAggregatedMarketFit,
      setAggregatedPublicationSummary,
      setTaskTemplates,
      updateTaskTemplateInList,
      setIsLlmConfigModalOpen,
      setLlmConfigModalLoading,
      setLlmConfigModalError,
      setAdminLlmConfigs,
      addAdminLlmConfig,
      updateAdminLlmConfig,
      deleteAdminLlmConfig,
      setIsLlmAdminModalOpen,
      setLlmAdminModalLoading,
      setLlmAdminModalError,
      setTasks,
      addTask,
      toggleTaskCompletion,
      deleteTaskById,
      setSelectedDate,
      setQueryMode,
      addChat,
      deleteChatById,
      fetchUserChats,
      updateChatTitle,
      setChatInProgress,
      setUserProfile,
      setIsAdminTasksModalOpen, // Expose dispatcher
      setAdminTasksLoading, // Expose dispatcher
      setAdminTasksError, // Expose dispatcher
      setAllUsers, // Expose dispatcher (for initial load)
      addUserToAllUsersList, // Expose dispatcher (for signup success)
      removeUserFromAllUsersList, // Expose dispatcher (for delete success)
      updateEmailPageWork,
      clearEmailPageWork,
      setLoading,
      setIsLoadingQueryData,
      setIsAnalyticsModalOpen,
      setAnalyticsDashboardLoading,
      setAnalyticsDashboardError,
      setAnalyticsDashboardData,
      setLastAnalyticsQuery,
      setAnalyticsSelectedChartType,

      // Error Handling
      apiErrors: state.apiErrors, //Expose individual API errors
      setError, // Expose setError function
      clearError, // Expose clearError function
      setApiError, //Expose individual API errors
      clearApiError,
      setMetaTokenExpired,
      fetchUsers, // Expose API wrapper
      signupUserAdmin, // Expose API wrapper
      moveInfluencers, // Expose API wrapper
      deleteUserAdmin, // Expose API wrapper
      fetchComplianceStatus,
      acceptLegalAgreement,
      importInfluencers,
      moveInfluencersToHold,
      permanentlyDiscardInfluencers,
      batchRefreshOnHoldInfluencers,
      analyzeOnHoldInfluencer,
      singleRefreshOnHoldInfluencer,
      setIsDiscoverySettingsModalOpen,
      setDiscoverySorting,
      fetchAllCountries,
      fetchDiscoverySettings,
      updateDiscoverySettings,
      generateDiscoveryKeywords,
      fetchInfluencerPageData,
      fetchInfluencerData,
      updateUserProfile,
      fetchProductsUsed,
      updateProducts,
      fetchSocialMedias,
      fetchSocialContacts,
      addSocialContact,
      addSocialMedia,
      updateSocialContact,
      updateSocialMedia,
      updatePartnerInfo,
      addNewInfluencer,
      getInfluencerDetails,
      deleteInfluencer,
      refreshInfluencerHealth,
      fetchInfluencerHealthDashboard,
      refreshInfluencerContentFocus, // NEW
      fetchInfluencerContentFocusDashboard, // NEW
      runSingleQuery,
      runMultipleQuery,
      fetchCompanyData,
      updateCompanyData,
      fetchProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      fetchPublicationsByInfluencer,
      createPublication,
      deletePublication,
      fetchProductsToPublish,
      togglePublishedProduct,
      addPublishedProduct,
      deletePublishedProduct,
      toggleIncludeVideoData,
      refreshPublicationMetadata,
      generateAISummary,
      fetchInfluencerAiSummary,
      updateAISummary,
      generatePublicationAISummary,
      fetchPublicationAiSummary,
      updatePublicationAISummary,
      generatePrivatePublicationAISummary,
      generateMessagesSummary,
      fetchMessagesSummary,
      saveMessagesSummary, // New function, added to contextValue
      updateActiveChannel,
      updateInfluencerStatus,
      fetchNewEmails,
      generateEmailDraft,
      translateEmailDraft,
      simpleTranslation,
      sendEmail,
      obtainNewPartnerData,
      saveConfirmedPartnerData,
      fetchTaskTemplates,
      updateTaskTemplate,
      createTask,
      updateTask,
      deleteTask,
      rescheduleTasks,
      fetchTasks,
      fetchBatchInfluencerData,
      fetchPendingEmailTask,
      fetchLlmConfigurations,
      fetchAdminLlmConfigurations,
      fetchUserSettings,
      addLlmConfiguration,
      updateLlmConfiguration,
      deleteLlmConfiguration,
      updateUserSettings,
      fetchUserApiKeyStatus,
      upsertUserApiKey,
      deleteUserApiKey,
      // Provide API functions directly if preferred over wrappers in context
      apiFetchLLMOptions,
      apiFetchChatHistory,
      apiCreateChat,
      apiSendMessage,
      apiSetChatTitle,
      apiDeleteChat,

      // Social Media
      fetchSocialMediaConnections,
      setSocialMediaConnections,
      connectGoogle,
      connectMicrosoft365,
      connectMeta,
      connectTelegram,
      connectYouTube,
      disconnectYouTube,
      refreshYouTubeToken,
      initTelegramAuth,
      completeTelegramAuth,
      completeTelegram2FAAuth,
      disconnectGoogle,
      disconnectMicrosoft365,
      disconnectMeta,
      disconnectTelegram,
      listGoogleAccounts,
      listMicrosoft365Accounts,
      listMetaAccounts,
      listTelegramAccounts,
      getGoogleStatus,
      getMicrosoft365Status,
      getMetaStatus,
      getTelegramStatus,
      refreshGoogleToken,
      refreshMetaToken,
      refreshMicrosoft365Token,
      handleRetryConnection,
      fetchAnalyticsData,
      updateAiAgentPageCache,
      clearAiAgentPageCache,
      isSuperAdminModalOpen,
      superAdminLoading,
      superAdminError,
      systemUsers,
      systemInvoices,
      userInvoices,
      companyWalletTransactions,
      systemWalletTransactions,
      setIsSuperAdminModalOpen,
      fetchAllSystemUsers,
      onboardNewCompany,
      executeImpersonation,
      fetchSystemInvoices,
      superAdminScrapeWebsite,
      markInvoiceAsPaid,
      fetchUserInvoices,
      generateMonthlyInvoices,
      fetchCompanyWalletTransactions,
      fetchSystemWalletTransactions,
      handleStripeCheckout,
      handleStripeSetup,
      loadAutoTopupConfig,
      saveAutoTopupConfig,
    }),
    [
      // Existing Dependencies
      state,
      // Dispatcher Functions (add dispatch)
      retrieveUserProfile,
      setIsOffline,
      productToggles,
      setProductToggles,
      setQueryResults,
      setLastQuery,
      setQueryTableSorting,
      setActiveinfluencerId,
      setActiveChannel,
      setStatus,
      setActiveInfluencerAndChannel,
      setReconnectNetwork,
      setIsUserProfileModalOpen,
      setIsUserProfileModalLoading,
      setUserProfileModalError,
      setIsCompanyModalOpen,
      setCompanyData,
      setIsCompanyModalLoading,
      setCompanyModalError,
      setProducts,
      addProductToList,
      updateProductInList,
      removeProductFromList,
      setIsProductModalOpen,
      setSelectedProductId,
      setProductModalLoading,
      setProductModalError,
      setIsMarketFitModalOpen,
      setMarketFitDashboardData,
      setIsMarketFitDashboardLoading,
      setIsContentFocusModalOpen,
      setContentFocusDashboardData,
      setIsContentFocusDashboardLoading,
      setPublications,
      setPublicationTogglingVideo,
      updatePublicationVideoData,
      setPublicationTogglingProduct,
      updatePublicationProducts,
      setPublicationGeneratingSummary, // Expose new dispatcher
      setPublicationAiSummary, // Expose new dispatcher
      generateSingleMarketFitAnalysis,
      fetchIndividualMarketFitAnalysis,
      setIndividualMarketFitAnalysis,
      clearAggregatedData,
      fetchAggregatedMarketFit,
      fetchAggregatedPublicationSummary,
      setAggregatedMarketFit,
      setAggregatedPublicationSummary,
      setTaskTemplates,
      updateTaskTemplateInList,
      setIsLlmConfigModalOpen,
      setLlmConfigModalLoading,
      setLlmConfigModalError,
      setAdminLlmConfigs,
      addAdminLlmConfig,
      updateAdminLlmConfig,
      deleteAdminLlmConfig,
      setIsLlmAdminModalOpen,
      setLlmAdminModalLoading,
      setLlmAdminModalError,
      setTasks,
      addTask,
      toggleTaskCompletion,
      deleteTaskById,
      setSelectedDate,
      setQueryMode,
      addChat,
      deleteChatById,
      fetchUserChats,
      updateChatTitle,
      setChatInProgress,
      setUserProfile,
      updateEmailPageWork,
      clearEmailPageWork,
      setIsAdminTasksModalOpen, // Expose dispatcher
      setAdminTasksLoading, // Expose dispatcher
      setAdminTasksError, // Expose dispatcher
      setAllUsers, // Expose dispatcher (for initial load)
      addUserToAllUsersList, // Expose dispatcher (for signup success)
      removeUserFromAllUsersList, // Expose dispatcher (for delete success)
      setLoading,
      setIsLoadingQueryData,
      setIsAnalyticsModalOpen,
      setAnalyticsDashboardLoading,
      setAnalyticsDashboardError,
      setAnalyticsDashboardData,
      setLastAnalyticsQuery,
      setAnalyticsSelectedChartType,
      setError,
      clearError,
      setApiError,
      clearApiError,
      setMetaTokenExpired,
      fetchUsers, // Expose API wrapper
      signupUserAdmin, // Expose API wrapper
      moveInfluencers, // Expose API wrapper
      deleteUserAdmin, // Expose API wrapper
      fetchComplianceStatus,
      acceptLegalAgreement,
      importInfluencers,
      moveInfluencersToHold,
      permanentlyDiscardInfluencers,
      batchRefreshOnHoldInfluencers,
      analyzeOnHoldInfluencer,
      singleRefreshOnHoldInfluencer,
      setIsDiscoverySettingsModalOpen,
      fetchAllCountries,
      fetchDiscoverySettings,
      updateDiscoverySettings,
      generateDiscoveryKeywords,
      setDiscoverySorting,
      fetchInfluencerPageData,
      fetchInfluencerData,
      updateUserProfile,
      fetchProductsUsed,
      updateProducts,
      fetchSocialMedias,
      fetchSocialContacts,
      addSocialContact,
      addSocialMedia,
      updateSocialContact,
      updateSocialMedia,
      updatePartnerInfo,
      addNewInfluencer,
      getInfluencerDetails,
      deleteInfluencer,
      refreshInfluencerHealth,
      fetchInfluencerHealthDashboard,
      refreshInfluencerContentFocus, // NEW
      fetchInfluencerContentFocusDashboard, // NEW
      runSingleQuery,
      runMultipleQuery,
      fetchCompanyData,
      updateCompanyData,
      fetchProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      fetchPublicationsByInfluencer,
      createPublication,
      deletePublication,
      fetchProductsToPublish,
      togglePublishedProduct,
      addPublishedProduct,
      deletePublishedProduct,
      toggleIncludeVideoData,
      refreshPublicationMetadata,
      generateAISummary,
      fetchInfluencerAiSummary,
      updateAISummary,
      generatePublicationAISummary,
      fetchPublicationAiSummary,
      updatePublicationAISummary,
      generatePrivatePublicationAISummary,
      generateMessagesSummary,
      fetchMessagesSummary,
      saveMessagesSummary,
      updateActiveChannel,
      updateInfluencerStatus,
      fetchNewEmails,
      generateEmailDraft,
      translateEmailDraft,
      simpleTranslation,
      sendEmail,
      obtainNewPartnerData,
      saveConfirmedPartnerData,
      fetchTaskTemplates,
      updateTaskTemplate,
      createTask,
      updateTask,
      deleteTask,
      rescheduleTasks,
      fetchTasks,
      fetchBatchInfluencerData,
      fetchPendingEmailTask,
      fetchLlmConfigurations,
      fetchAdminLlmConfigurations,
      fetchUserSettings,
      addLlmConfiguration,
      updateLlmConfiguration,
      deleteLlmConfiguration,
      updateUserSettings,
      fetchUserApiKeyStatus,
      upsertUserApiKey,
      deleteUserApiKey,

      // Social Media
      fetchSocialMediaConnections,
      setSocialMediaConnections,
      connectGoogle,
      connectMicrosoft365,
      connectYouTube,
      disconnectYouTube,
      refreshYouTubeToken,
      connectMeta,
      connectTelegram,
      initTelegramAuth,
      completeTelegramAuth,
      completeTelegram2FAAuth,
      disconnectGoogle,
      disconnectMicrosoft365,
      disconnectMeta,
      disconnectTelegram,
      listGoogleAccounts,
      listMetaAccounts,
      listTelegramAccounts,
      getGoogleStatus,
      getMicrosoft365Status,
      getMetaStatus,
      getTelegramStatus,
      refreshGoogleToken,
      refreshMicrosoft365Token,
      refreshMetaToken,
      handleRetryConnection,
      fetchAnalyticsData,
      updateAiAgentPageCache,
      clearAiAgentPageCache,
      isSuperAdminModalOpen,
      superAdminLoading,
      superAdminError,
      systemUsers,
      systemInvoices,
      userInvoices,
      companyWalletTransactions,
      systemWalletTransactions,
      setIsSuperAdminModalOpen,
      fetchAllSystemUsers,
      onboardNewCompany,
      executeImpersonation,
      fetchSystemInvoices,
      superAdminScrapeWebsite,
      markInvoiceAsPaid,
      fetchUserInvoices,
      generateMonthlyInvoices,
      fetchCompanyWalletTransactions,
      fetchSystemWalletTransactions,
      handleStripeCheckout,
      handleStripeSetup,
      loadAutoTopupConfig,
      saveAutoTopupConfig,
    ],
  );

  return (
    <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
